import { useEffect, useState, useRef, useMemo } from "react";
import Swal from "sweetalert2";
import ReactQuill from "react-quill-new";
import { usePagination } from "../hooks/usePagination";
import UniversalPagination from "../common/UniversalPagination";
import "react-quill-new/dist/quill.snow.css";
import { API_BASE } from "../config/api";

import Sidebar from '../components/Sidebar'
import Header from '../components/Header'

function VideoList() {

    const [instructors, setInstructors] = useState([]);
    const [loadingInstructors, setLoadingInstructors] = useState(false);
    const [savingVideo, setSavingVideo] = useState(false);
    const [videos, setVideos] = useState([]);
    const [loadingVideos, setLoadingVideos] = useState(false);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [loadingViewVideo, setLoadingViewVideo] = useState(false);
    const [loadingEditVideo, setLoadingEditVideo] = useState(false);
    const [tagsCatalog, setTagsCatalog] = useState([]);
    const [loadingTags, setLoadingTags] = useState(false);

    const [selectedTags, setSelectedTags] = useState([]);
    const [editSelectedTags, setEditSelectedTags] = useState([]);

    const [videoSearchTerm, setVideoSearchTerm] = useState("");
    const [videoSortOrder, setVideoSortOrder] = useState("default");

    const [videoForm, setVideoForm] = useState({
        Video_Titulo: "",
        Instructor_ID: "",
        Video_Descripcion: "",
        Video_URL_Vimeo: "",
        Video_Duracion_Seg: "",
        Video_Tiene_Evaluacion: 0,
        Video_Tiene_Recursos: 0,
        Video_Miniatura: null,
        Video_Miniatura_Sitio: null,
    });

    const [successMessage, setSuccessMessage] = useState("");
    const [infoMessage, setInfoMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const [showEvaluationConfig, setShowEvaluationConfig] = useState(true);
    const [showResourcesConfig, setShowResourcesConfig] = useState(true);
    const [videoResources, setVideoResources] = useState([]);
    const [showEditResourcesConfig, setShowEditResourcesConfig] = useState(true);
    const [editVideoResources, setEditVideoResources] = useState([]);

    const resetMessages = () => {
        setSuccessMessage("");
        setInfoMessage("");
        setErrorMessage("");
    };

    const resetVideoForm = () => {
        setVideoForm({
            Video_Titulo: "",
            Instructor_ID: "",
            Video_Descripcion: "",
            Video_URL_Vimeo: "",
            Video_Duracion_Seg: "",
            Video_Tiene_Evaluacion: 0,
            Video_Tiene_Recursos: 0,
            Video_Miniatura: null,
            Video_Miniatura_Sitio: null,
        });

        setSelectedTags([]);
        setEvaluacionForm(getInitialEvaluacionForm("", ""));
        setShowEvaluationConfig(true);
        setShowResourcesConfig(true);
        setVideoResources([]);

        const miniaturaInput = document.getElementById("customFileVideo");
        const miniaturaSitioInput = document.getElementById("customFileVideoSite");

        if (miniaturaInput) miniaturaInput.value = "";
        if (miniaturaSitioInput) miniaturaSitioInput.value = "";
    };

    const loadVideoTags = async () => {
        try {
            setLoadingTags(true);
            setErrorMessage("");

            const response = await fetch(`${API_BASE}/api/admin/videoList/tags`, {
                method: "GET",
                credentials: "include",
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data?.message || "No fue posible obtener los tags");
            }

            setTagsCatalog(data?.data || []);
        } catch (error) {
            console.error("Error al cargar tags:", error);
            setErrorMessage(error.message || "Ocurrió un error al cargar los tags");
        } finally {
            setLoadingTags(false);
        }
    };

    const loadVideos = async () => {
        try {
            setLoadingVideos(true);
            setErrorMessage("");

            const response = await fetch(`${API_BASE}/api/admin/videoList`, {
                method: "GET",
                credentials: "include",
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data?.message || "No fue posible obtener los videos");
            }

            setVideos(data?.data || []);
        } catch (error) {
            console.error("Error al cargar videos:", error);
            setErrorMessage(error.message || "Ocurrió un error al cargar los videos");
        } finally {
            setLoadingVideos(false);
        }
    };

    useEffect(() => {
        loadVideoInstructors();
        loadVideoTags();
        loadVideos();
    }, []);

    const loadVideoInstructors = async () => {
        try {
            setLoadingInstructors(true);
            setErrorMessage("");

            const response = await fetch(`${API_BASE}/api/admin/videoList/instructors`, {
                method: "GET",
                credentials: "include",
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data?.message || "No fue posible obtener los instructores");
            }

            setInstructors(data?.data || []);
        } catch (error) {
            console.error("Error al cargar instructores:", error);
            setErrorMessage(error.message || "Ocurrió un error al cargar los instructores");
        } finally {
            setLoadingInstructors(false);
        }
    };

    const renderVideoStatus = (videoActivo) => {
        if (Number(videoActivo) === 1) {
            return (
                <span className="badge bg-success-subtle p-2 text-success">
                    Activo
                </span>
            );
        }

        return (
            <span className="badge bg-danger-subtle p-2 text-danger">
                Inactivo
            </span>
        );
    };

    const handleTagToggle = (tagId) => {
        setSelectedTags((prev) => {
            const exists = prev.includes(tagId);

            if (exists) {
                return prev.filter((id) => id !== tagId);
            }

            return [...prev, tagId];
        });
    };

    const handleEditTagToggle = (tagId) => {
        setEditSelectedTags((prev) => {
            const exists = prev.includes(tagId);

            if (exists) {
                return prev.filter((id) => id !== tagId);
            }

            return [...prev, tagId];
        });
    };

    const handleVideoInputChange = (e) => {
        const { name, value, type, checked, files } = e.target;

        if (type === "file") {
            setVideoForm((prev) => ({
                ...prev,
                [name]: files?.[0] || null,
            }));
            return;
        }

        if (type === "checkbox") {
            const checkedValue = checked ? 1 : 0;

            setVideoForm((prev) => ({
                ...prev,
                [name]: checkedValue,
            }));

            if (name === "Video_Tiene_Evaluacion") {
                if (checkedValue === 1) {
                    setEvaluacionForm((prev) => {
                        const hasPreguntas =
                            Array.isArray(prev?.preguntas) && prev.preguntas.length > 0;

                        return hasPreguntas
                            ? {
                                ...prev,
                                Evaluacion_Titulo: videoForm.Video_Titulo?.trim()
                                    ? `Evaluación - ${videoForm.Video_Titulo.trim()}`
                                    : "Evaluación - ",
                                Evaluacion_Descripcion: videoForm.Video_Descripcion || "",
                            }
                            : getInitialEvaluacionForm(
                                videoForm.Video_Titulo,
                                videoForm.Video_Descripcion
                            );
                    });
                } else {
                    setEvaluacionForm(
                        getInitialEvaluacionForm(
                            videoForm.Video_Titulo,
                            videoForm.Video_Descripcion
                        )
                    );
                }
            }

            return;
        }

        setVideoForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleDescriptionChange = (value) => {
        setVideoForm((prev) => ({
            ...prev,
            Video_Descripcion: value,
        }));
    };

    const validateVideoForm = () => {
        if (!videoForm.Video_Titulo.trim()) {
            setInfoMessage("El campo título es obligatorio.");
            return false;
        }

        if (!videoForm.Instructor_ID) {
            setInfoMessage("El campo experto es obligatorio.");
            return false;
        }

        if (!videoForm.Video_Descripcion || !videoForm.Video_Descripcion.trim() || videoForm.Video_Descripcion === "<p><br></p>") {
            setInfoMessage("El campo descripción es obligatorio.");
            return false;
        }

        if (!videoForm.Video_URL_Vimeo.trim()) {
            setInfoMessage("El campo ID Vimeo es obligatorio.");
            return false;
        }

        if (!videoForm.Video_Duracion_Seg.trim()) {
            setInfoMessage("El campo duración aproximada es obligatorio.");
            return false;
        }

        if (!/^\d+$/.test(videoForm.Video_Duracion_Seg.trim())) {
            setInfoMessage("La duración aproximada debe ser un número entero.");
            return false;
        }

        if (!validateEvaluacionForm()) {
            return false;
        }

        return true;
    };

    const addResource = () => {
        setVideoResources((prev) => [
            ...prev,
            createEmptyResource(prev.length + 1),
        ]);
    };

    const removeResource = (tempId) => {
        setVideoResources((prev) =>
            prev
                .filter((item) => item.tempId !== tempId)
                .map((item, index) => ({
                    ...item,
                    Recurso_Orden: index + 1,
                }))
        );
    };

    const handleResourceChange = (tempId, field, value) => {
        setVideoResources((prev) =>
            prev.map((item) =>
                item.tempId === tempId
                    ? {
                        ...item,
                        [field]: value,
                    }
                    : item
            )
        );
    };

    const handleResourceTypeChange = (tempId, tipo) => {
        setVideoResources((prev) =>
            prev.map((item) =>
                item.tempId === tempId
                    ? {
                        ...item,
                        Recurso_Tipo: tipo,
                        Recurso_URL: tipo === "link" ? item.Recurso_URL : "",
                        resourceFile: tipo === "archivo" ? item.resourceFile : null,
                        Recurso_Nombre_Archivo: tipo === "archivo" ? item.Recurso_Nombre_Archivo : "",
                    }
                    : item
            )
        );
    };

    const handleResourceImageFileChange = (tempId, file) => {
        setVideoResources((prev) =>
            prev.map((item) =>
                item.tempId === tempId
                    ? {
                        ...item,
                        resourceImageFile: file || null,
                    }
                    : item
            )
        );
    };

    const handleResourceFileChange = (tempId, file) => {
        setVideoResources((prev) =>
            prev.map((item) =>
                item.tempId === tempId
                    ? {
                        ...item,
                        resourceFile: file || null,
                        Recurso_Nombre_Archivo: file?.name || "",
                    }
                    : item
            )
        );
    };

    // Funcion para agregar evaluaciones

    const getEmptyRespuesta = (orden = 1) => ({
        Respuesta_Texto: "",
        Respuesta_Orden: orden,
        Respuesta_Correcta: 0,
    });

    const getEmptyPregunta = (orden = 1) => ({
        Pregunta_Texto: "",
        Pregunta_Orden: orden,
        Pregunta_Puntos_Estreno: 1,
        Pregunta_Puntos_Normal: 1,
        respuestas: [
            getEmptyRespuesta(1),
            getEmptyRespuesta(2),
            getEmptyRespuesta(3),
            getEmptyRespuesta(4),
        ],
    });

    const getInitialEvaluacionForm = (videoTitulo = "", videoDescripcion = "") => ({
        Evaluacion_Titulo: videoTitulo?.trim()
            ? `Evaluación - ${videoTitulo.trim()}`
            : "Evaluación - ",
        Evaluacion_Descripcion: videoDescripcion || "",
        preguntas: [getEmptyPregunta(1), getEmptyPregunta(2)],
    });

    const [evaluacionForm, setEvaluacionForm] = useState(
        getInitialEvaluacionForm("", "")
    );

    // Este efecto logra que el titulo y descripcion se sincronocen con lo que escribireron en agregar video

    useEffect(() => {
        if (Number(videoForm.Video_Tiene_Evaluacion) !== 1) return;

        setEvaluacionForm((prev) => ({
            ...prev,
            Evaluacion_Titulo: videoForm.Video_Titulo?.trim()
                ? `Evaluación - ${videoForm.Video_Titulo.trim()}`
                : "Evaluación - ",
            Evaluacion_Descripcion: videoForm.Video_Descripcion || "",
        }));
    }, [
        videoForm.Video_Tiene_Evaluacion,
        videoForm.Video_Titulo,
        videoForm.Video_Descripcion,
    ]);

    // Funcion para preguntas 
    const handleEvaluacionFieldChange = (e) => {
        const { name, value } = e.target;

        setEvaluacionForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handlePreguntaChange = (preguntaIndex, field, value) => {
        setEvaluacionForm((prev) => {
            const preguntas = [...prev.preguntas];
            preguntas[preguntaIndex] = {
                ...preguntas[preguntaIndex],
                [field]:
                    field === "Pregunta_Orden" ||
                    field === "Pregunta_Puntos_Estreno" ||
                    field === "Pregunta_Puntos_Normal"
                        ? value === ""
                            ? ""
                            : Number(value)
                        : value,
            };

            return {
                ...prev,
                preguntas,
            };
        });
    };

    const handleRespuestaChange = (preguntaIndex, respuestaIndex, value) => {
        setEvaluacionForm((prev) => {
            const preguntas = [...prev.preguntas];
            const respuestas = [...preguntas[preguntaIndex].respuestas];

            respuestas[respuestaIndex] = {
                ...respuestas[respuestaIndex],
                Respuesta_Texto: value,
            };

            preguntas[preguntaIndex] = {
                ...preguntas[preguntaIndex],
                respuestas,
            };

            return {
                ...prev,
                preguntas,
            };
        });
    };

    const handleCorrectaChange = (preguntaIndex, respuestaIndex) => {
        setEvaluacionForm((prev) => {
            const preguntas = [...prev.preguntas];
            const respuestas = preguntas[preguntaIndex].respuestas.map((respuesta, index) => ({
                ...respuesta,
                Respuesta_Correcta: index === respuestaIndex ? 1 : 0,
            }));

            preguntas[preguntaIndex] = {
                ...preguntas[preguntaIndex],
                respuestas,
            };

            return {
                ...prev,
                preguntas,
            };
        });
    };

    const addPregunta = () => {
        setEvaluacionForm((prev) => ({
            ...prev,
            preguntas: [
                ...prev.preguntas,
                getEmptyPregunta(prev.preguntas.length + 1),
            ],
        }));
    };

    const removePregunta = (preguntaIndex) => {
        setEvaluacionForm((prev) => {
            const preguntas = prev.preguntas
                .filter((_, index) => index !== preguntaIndex)
                .map((pregunta, index) => ({
                    ...pregunta,
                    Pregunta_Orden: index + 1,
                }));

            return {
                ...prev,
                preguntas: preguntas.length > 0 ? preguntas : [getEmptyPregunta(1)],
            };
        });
    };

    const validateEvaluacionForm = () => {
        if (Number(videoForm.Video_Tiene_Evaluacion) !== 1) return true;

        if (!evaluacionForm.Evaluacion_Titulo.trim()) {
            setInfoMessage("El título de la evaluación es obligatorio.");
            return false;
        }

        if (!Array.isArray(evaluacionForm.preguntas) || evaluacionForm.preguntas.length < 1) {
            setInfoMessage("Debes agregar al menos una pregunta.");
            return false;
        }

        for (let i = 0; i < evaluacionForm.preguntas.length; i += 1) {
            const pregunta = evaluacionForm.preguntas[i];

            if (!pregunta.Pregunta_Texto.trim()) {
                setInfoMessage(`La pregunta ${i + 1} debe tener texto.`);
                return false;
            }

            if (
                pregunta.Pregunta_Orden === "" ||
                pregunta.Pregunta_Orden === null ||
                Number(pregunta.Pregunta_Orden) < 1
            ) {
                setInfoMessage(`La pregunta ${i + 1} debe tener un orden válido.`);
                return false;
            }

            if (
                pregunta.Pregunta_Puntos_Estreno === "" ||
                pregunta.Pregunta_Puntos_Estreno === null ||
                Number(pregunta.Pregunta_Puntos_Estreno) < 0
            ) {
                setInfoMessage(`La pregunta ${i + 1} debe tener puntos de estreno válidos.`);
                return false;
            }

            if (
                pregunta.Pregunta_Puntos_Normal === "" ||
                pregunta.Pregunta_Puntos_Normal === null ||
                Number(pregunta.Pregunta_Puntos_Normal) < 0
            ) {
                setInfoMessage(`La pregunta ${i + 1} debe tener puntos normales válidos.`);
                return false;
            }

            if (!Array.isArray(pregunta.respuestas) || pregunta.respuestas.length !== 4) {
                setInfoMessage(`La pregunta ${i + 1} debe tener exactamente 4 respuestas.`);
                return false;
            }

            const correctas = pregunta.respuestas.filter(
                (respuesta) => Number(respuesta.Respuesta_Correcta) === 1
            );

            if (correctas.length !== 1) {
                setInfoMessage(`La pregunta ${i + 1} debe tener una sola respuesta correcta.`);
                return false;
            }

            for (let j = 0; j < pregunta.respuestas.length; j += 1) {
                if (!pregunta.respuestas[j].Respuesta_Texto.trim()) {
                    setInfoMessage(
                        `La respuesta ${j + 1} de la pregunta ${i + 1} no puede estar vacía.`
                    );
                    return false;
                }
            }
        }

        return true;
    };

   const handleSubmitVideo = async (e) => {
    e.preventDefault();
    resetMessages();

    const isValid = validateVideoForm();
    if (!isValid) return;

    try {
        setSavingVideo(true);

        const formData = new FormData();

        formData.append("Video_Titulo", String(videoForm.Video_Titulo || "").trim());
        formData.append("Instructor_ID", videoForm.Instructor_ID);
        formData.append("Video_Descripcion", videoForm.Video_Descripcion || "");
        formData.append("Video_URL_Vimeo", String(videoForm.Video_URL_Vimeo || "").trim());
        formData.append("Video_Duracion_Seg", String(videoForm.Video_Duracion_Seg || "").trim());
        formData.append(
            "Video_Tiene_Evaluacion",
            Number(videoForm.Video_Tiene_Evaluacion) === 1 ? 1 : 0
        );
        formData.append(
            "Video_Tiene_Recursos",
            Number(videoForm.Video_Tiene_Recursos) === 1 ? 1 : 0
        );
        formData.append("tags", JSON.stringify(selectedTags));

        if (videoForm.Video_Miniatura) {
            formData.append("Video_Miniatura", videoForm.Video_Miniatura);
        }

        if (videoForm.Video_Miniatura_Sitio) {
            formData.append("Video_Miniatura_Sitio", videoForm.Video_Miniatura_Sitio);
        }

        if (Number(videoForm.Video_Tiene_Recursos) === 1) {
            if (videoResources.length === 0) {
                throw new Error("Debes agregar al menos un recurso si el video tendrá recursos");
            }

            for (const resource of videoResources) {
                if (!String(resource.Recurso_Titulo || "").trim()) {
                    throw new Error("Todos los recursos deben tener título");
                }

                if (resource.Recurso_Tipo === "link") {
                    if (!String(resource.Recurso_URL || "").trim()) {
                        throw new Error(`El recurso "${resource.Recurso_Titulo}" debe tener una URL`);
                    }
                }

                if (resource.Recurso_Tipo === "archivo") {
                    if (!resource.resourceFile && !resource.Recurso_URL) {
                        throw new Error(`El recurso "${resource.Recurso_Titulo}" debe tener un archivo`);
                    }
                }
            }
        }

        if (Number(videoForm.Video_Tiene_Evaluacion) === 1) {
            formData.append("evaluacion", JSON.stringify(evaluacionForm));
        }

        if (Number(videoForm.Video_Tiene_Recursos) === 1) {
            formData.append(
                "resources",
                JSON.stringify(
                    videoResources.map((resource, index) => ({
                        tempId: resource.tempId,
                        Recurso_ID: resource.Recurso_ID || null,
                        Recurso_Titulo: String(resource.Recurso_Titulo || "").trim(),
                        Recurso_Tipo: resource.Recurso_Tipo,
                        Recurso_URL:
                            resource.Recurso_Tipo === "link"
                                ? String(resource.Recurso_URL || "").trim()
                                : String(resource.Recurso_URL || "").trim(),
                        Recurso_Imagen: resource.Recurso_Imagen || "",
                        Recurso_Orden: Number(resource.Recurso_Orden || index + 1),
                        Recurso_Nombre_Archivo: resource.Recurso_Nombre_Archivo || "",
                    }))
                )
            );

            videoResources.forEach((resource) => {
                if (resource.resourceImageFile) {
                    formData.append(`resourceImage_${resource.tempId}`, resource.resourceImageFile);
                }

                if (resource.Recurso_Tipo === "archivo" && resource.resourceFile) {
                    formData.append(`resourceFile_${resource.tempId}`, resource.resourceFile);
                }
            });
        }

        const response = await fetch(`${API_BASE}/api/admin/videoList`, {
            method: "POST",
            credentials: "include",
            body: formData,
        });

        const data = await response.json();

        if (!response.ok) {
            if (response.status === 400 || response.status === 404) {
                setInfoMessage(data?.message || "Verifica la información del formulario.");
                return;
            }

            throw new Error(data?.message || "No fue posible agregar el video");
        }

        await loadVideos();

        setSuccessMessage(data?.message || "Se agregó el video con éxito.");
        resetVideoForm();
    } catch (error) {
        console.error("Error al agregar video:", error);
        setErrorMessage(error.message || "Ocurrió un error al agregar el video");
    } finally {
        setSavingVideo(false);
    }
};

    // Funcion para ver Info del Video 
    const handleOpenViewVideo = async (video) => {
    if (!video?.Video_ID) return;

    try {
        setLoadingViewVideo(true);

        const response = await fetch(
            `${API_BASE}/api/admin/videoList/${video.Video_ID}`,
            {
                method: "GET",
                credentials: "include",
            }
        );

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data?.message || "No fue posible obtener el detalle del video");
        }

        setSelectedVideo(data?.data || null);
    } catch (error) {
        console.error("Error al cargar detalle del video:", error);
        setSelectedVideo(video || null);
    } finally {
        setLoadingViewVideo(false);
    }
};

    // Funcion para Editar info del Video 
    const [editVideoForm, setEditVideoForm] = useState({
        Video_ID: null,
        Video_Titulo: "",
        Instructor_ID: "",
        Video_Descripcion: "",
        Video_URL_Vimeo: "",
        Video_Duracion_Seg: "",
        Video_Tiene_Evaluacion: 0,
        Video_Tiene_Recursos: 0,
        Video_Miniatura: null,
        Video_Miniatura_Sitio: null,
        Current_Video_Miniatura: "",
        Current_Video_Miniatura_Sitio: "",
    });

    const [savingEditVideo, setSavingEditVideo] = useState(false);

    const [editSuccessMessage, setEditSuccessMessage] = useState("");
    const [editInfoMessage, setEditInfoMessage] = useState("");
    const [editErrorMessage, setEditErrorMessage] = useState("");

    const getEmptyEditRespuesta = (orden = 1) => ({
        Respuesta_Texto: "",
        Respuesta_Orden: orden,
        Respuesta_Correcta: 0,
    });

    const getEmptyEditPregunta = (orden = 1) => ({
        Pregunta_Texto: "",
        Pregunta_Orden: orden,
        Pregunta_Puntos_Estreno: 1,
        Pregunta_Puntos_Normal: 1,
        respuestas: [
            getEmptyEditRespuesta(1),
            getEmptyEditRespuesta(2),
            getEmptyEditRespuesta(3),
            getEmptyEditRespuesta(4),
        ],
    });

    const getInitialEditEvaluacionForm = (videoTitulo = "", videoDescripcion = "", preguntas = null) => ({
        Evaluacion_Titulo: videoTitulo?.trim()
            ? `Evaluación - ${videoTitulo.trim()}`
            : "Evaluación - ",
        Evaluacion_Descripcion: videoDescripcion || "",
        preguntas:
            Array.isArray(preguntas) && preguntas.length > 0
                ? preguntas
                : [getEmptyEditPregunta(1), getEmptyEditPregunta(2)],
    });

    const [editEvaluacionForm, setEditEvaluacionForm] = useState(
        getInitialEditEvaluacionForm("", "")
    );

    const resetEditMessages = () => {
        setEditSuccessMessage("");
        setEditInfoMessage("");
        setEditErrorMessage("");
    };

    const resetEditVideoForm = () => {
        setEditVideoForm({
            Video_ID: null,
            Video_Titulo: "",
            Instructor_ID: "",
            Video_Descripcion: "",
            Video_URL_Vimeo: "",
            Video_Duracion_Seg: "",
            Video_Tiene_Evaluacion: 0,
            Video_Miniatura: null,
            Video_Miniatura_Sitio: null,
            Current_Video_Miniatura: "",
            Current_Video_Miniatura_Sitio: "",
        });

        setEditSelectedTags([]);
        setEditEvaluacionForm(getInitialEditEvaluacionForm("", ""));

        const file1 = document.getElementById("editCustomFileVideo");
        const file2 = document.getElementById("editCustomFileVideoSite");

        if (file1) file1.value = "";
        if (file2) file2.value = "";
    };

    const normalizeEditPreguntas = (preguntas = []) => {
        if (!Array.isArray(preguntas) || preguntas.length === 0) {
            return [getEmptyEditPregunta(1), getEmptyEditPregunta(2)];
        }

        return preguntas.map((pregunta, preguntaIndex) => {
            let respuestas = Array.isArray(pregunta?.respuestas) ? [...pregunta.respuestas] : [];

            while (respuestas.length < 4) {
                respuestas.push(getEmptyEditRespuesta(respuestas.length + 1));
            }

            respuestas = respuestas.slice(0, 4).map((respuesta, respuestaIndex) => ({
                Respuesta_Texto: respuesta?.Respuesta_Texto || "",
                Respuesta_Orden: respuesta?.Respuesta_Orden || respuestaIndex + 1,
                Respuesta_Correcta: Number(respuesta?.Respuesta_Correcta) === 1 ? 1 : 0,
            }));

            const tieneCorrecta = respuestas.some(
                (respuesta) => Number(respuesta.Respuesta_Correcta) === 1
            );

            if (!tieneCorrecta && respuestas.length > 0) {
                respuestas[0].Respuesta_Correcta = 1;
            }

            return {
                Pregunta_Texto: pregunta?.Pregunta_Texto || "",
                Pregunta_Orden: pregunta?.Pregunta_Orden || preguntaIndex + 1,
                Pregunta_Puntos_Estreno:
                    pregunta?.Pregunta_Puntos_Estreno !== undefined
                        ? Number(pregunta.Pregunta_Puntos_Estreno)
                        : 1,
                Pregunta_Puntos_Normal:
                    pregunta?.Pregunta_Puntos_Normal !== undefined
                        ? Number(pregunta.Pregunta_Puntos_Normal)
                        : 1,
                respuestas,
            };
        });
    };

    const handleOpenEditVideo = async (video) => {
        if (!video?.Video_ID) return;

        try {
            resetEditMessages();
            setLoadingEditVideo(true);

            const response = await fetch(
                `${API_BASE}/api/admin/videoList/${video.Video_ID}`,
                {
                    method: "GET",
                    credentials: "include",
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data?.message || "No fue posible obtener el detalle del video");
            }

            const detail = data?.data;

            setEditSelectedTags(
                Array.isArray(detail?.tags)
                    ? detail.tags.map((tag) => Number(tag.Tag_ID))
                    : []
            );

            const preguntasNormalizadas = normalizeEditPreguntas(
                detail?.evaluacion?.preguntas || []
            );

            setEditVideoForm({
                Video_ID: detail.Video_ID,
                Video_Titulo: detail.Video_Titulo || "",
                Instructor_ID: detail.Instructor_ID || "",
                Video_Descripcion: detail.Video_Descripcion || "",
                Video_URL_Vimeo: detail.Video_URL_Vimeo || "",
                Video_Duracion_Seg: detail.Video_Duracion_Seg
                    ? String(detail.Video_Duracion_Seg)
                    : "",
                Video_Tiene_Evaluacion: Number(detail.Video_Tiene_Evaluacion) === 1 ? 1 : 0,
                Video_Tiene_Recursos: Number(detail.Video_Tiene_Recursos) === 1 ? 1 : 0,
                Video_Miniatura: null,
                Video_Miniatura_Sitio: null,
                Current_Video_Miniatura: detail.Video_Miniatura || "",
                Current_Video_Miniatura_Sitio: detail.Video_Miniatura_Sitio || "",
            });

            setEditEvaluacionForm({
                Evaluacion_Titulo:
                    detail?.evaluacion?.Evaluacion_Titulo ||
                    (detail?.Video_Titulo?.trim()
                        ? `Evaluación - ${detail.Video_Titulo.trim()}`
                        : "Evaluación - "),
                Evaluacion_Descripcion:
                    detail?.evaluacion?.Evaluacion_Descripcion ||
                    detail?.Video_Descripcion ||
                    "",
                preguntas:
                    Number(detail?.Video_Tiene_Evaluacion) === 1
                        ? preguntasNormalizadas
                        : [getEmptyEditPregunta(1), getEmptyEditPregunta(2)],
            });

            setEditVideoResources(
                Array.isArray(detail?.resources)
                    ? detail.resources.map((resource, index) => ({
                        tempId: `edit_resource_${resource.Recurso_ID || index}_${Date.now()}_${Math.random()
                            .toString(16)
                            .slice(2)}`,
                        Recurso_ID: resource.Recurso_ID || null,
                        Recurso_Titulo: resource.Recurso_Titulo || "",
                        Recurso_Tipo: resource.Recurso_Tipo || "link",
                        Recurso_URL: resource.Recurso_URL || "",
                        Recurso_Imagen: resource.Recurso_Imagen || "",
                        Recurso_Orden: resource.Recurso_Orden
                            ? Number(resource.Recurso_Orden)
                            : index + 1,
                        resourceImageFile: null,
                        resourceFile: null,
                        Recurso_Nombre_Archivo: resource.Recurso_Nombre_Archivo || "",
                        Current_Recurso_Imagen: resource.Recurso_Imagen || "",
                    }))
                    : []
            );

            setShowEditResourcesConfig(true);
        } catch (error) {
            console.error("Error al abrir edición del video:", error);
            setEditErrorMessage(
                error.message || "Ocurrió un error al cargar el video para edición"
            );
        } finally {
            setLoadingEditVideo(false);
        }
    };

    useEffect(() => {
        if (Number(editVideoForm.Video_Tiene_Evaluacion) !== 1) return;

        setEditEvaluacionForm((prev) => ({
            ...prev,
            Evaluacion_Titulo: editVideoForm.Video_Titulo?.trim()
                ? `Evaluación - ${editVideoForm.Video_Titulo.trim()}`
                : "Evaluación - ",
            Evaluacion_Descripcion: editVideoForm.Video_Descripcion || "",
        }));
    }, [
        editVideoForm.Video_Tiene_Evaluacion,
        editVideoForm.Video_Titulo,
        editVideoForm.Video_Descripcion,
    ]);

    const handleEditVideoInputChange = (e) => {
        const { name, value, type, checked, files } = e.target;

        if (type === "file") {
            setEditVideoForm((prev) => ({
                ...prev,
                [name]: files?.[0] || null,
            }));
            return;
        }

        if (type === "checkbox") {
            const checkedValue = checked ? 1 : 0;

            setEditVideoForm((prev) => ({
                ...prev,
                [name]: checkedValue,
            }));

            if (name === "Video_Tiene_Evaluacion") {
                if (checkedValue === 1) {
                    setEditEvaluacionForm((prev) => {
                        const hasPreguntas =
                            Array.isArray(prev?.preguntas) && prev.preguntas.length > 0;

                        return hasPreguntas
                            ? {
                                ...prev,
                                Evaluacion_Titulo: editVideoForm.Video_Titulo?.trim()
                                    ? `Evaluación - ${editVideoForm.Video_Titulo.trim()}`
                                    : "Evaluación - ",
                                Evaluacion_Descripcion:
                                    editVideoForm.Video_Descripcion || "",
                            }
                            : getInitialEditEvaluacionForm(
                                editVideoForm.Video_Titulo,
                                editVideoForm.Video_Descripcion
                            );
                    });
                } else {
                    setEditEvaluacionForm(
                        getInitialEditEvaluacionForm(
                            editVideoForm.Video_Titulo,
                            editVideoForm.Video_Descripcion
                        )
                    );
                }
            }

            return;
        }

        setEditVideoForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleEditDescriptionChange = (value) => {
        setEditVideoForm((prev) => ({
            ...prev,
            Video_Descripcion: value,
        }));
    };

    const handleEditEvaluacionFieldChange = (e) => {
        const { name, value } = e.target;

        setEditEvaluacionForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleEditPreguntaChange = (preguntaIndex, field, value) => {
        setEditEvaluacionForm((prev) => {
            const preguntas = [...prev.preguntas];

            preguntas[preguntaIndex] = {
                ...preguntas[preguntaIndex],
                [field]:
                    field === "Pregunta_Orden" ||
                    field === "Pregunta_Puntos_Estreno" ||
                    field === "Pregunta_Puntos_Normal"
                        ? value === ""
                            ? ""
                            : Number(value)
                        : value,
            };

            return {
                ...prev,
                preguntas,
            };
        });
    };

    const handleEditRespuestaChange = (preguntaIndex, respuestaIndex, value) => {
        setEditEvaluacionForm((prev) => {
            const preguntas = [...prev.preguntas];
            const respuestas = [...preguntas[preguntaIndex].respuestas];

            respuestas[respuestaIndex] = {
                ...respuestas[respuestaIndex],
                Respuesta_Texto: value,
            };

            preguntas[preguntaIndex] = {
                ...preguntas[preguntaIndex],
                respuestas,
            };

            return {
                ...prev,
                preguntas,
            };
        });
    };

    const handleEditCorrectaChange = (preguntaIndex, respuestaIndex) => {
        setEditEvaluacionForm((prev) => {
            const preguntas = [...prev.preguntas];

            preguntas[preguntaIndex].respuestas = preguntas[preguntaIndex].respuestas.map(
                (respuesta, index) => ({
                    ...respuesta,
                    Respuesta_Correcta: index === respuestaIndex ? 1 : 0,
                })
            );

            return {
                ...prev,
                preguntas,
            };
        });
    };

    const addEditPregunta = () => {
        setEditEvaluacionForm((prev) => ({
            ...prev,
            preguntas: [
                ...prev.preguntas,
                getEmptyEditPregunta(prev.preguntas.length + 1),
            ],
        }));
    };

    const removeEditPregunta = (preguntaIndex) => {
        setEditEvaluacionForm((prev) => {
            const preguntas = prev.preguntas
                .filter((_, index) => index !== preguntaIndex)
                .map((pregunta, index) => ({
                    ...pregunta,
                    Pregunta_Orden: index + 1,
                }));

            return {
                ...prev,
                preguntas: preguntas.length > 0 ? preguntas : [getEmptyEditPregunta(1)],
            };
        });
    };

    const validateEditEvaluacionForm = () => {
        if (Number(editVideoForm.Video_Tiene_Evaluacion) !== 1) return true;

        if (!editEvaluacionForm.Evaluacion_Titulo.trim()) {
            setEditInfoMessage("El título de la evaluación es obligatorio.");
            return false;
        }

        if (
            !Array.isArray(editEvaluacionForm.preguntas) ||
            editEvaluacionForm.preguntas.length < 1
        ) {
            setEditInfoMessage("Debes agregar al menos una pregunta.");
            return false;
        }

        for (let i = 0; i < editEvaluacionForm.preguntas.length; i += 1) {
            const pregunta = editEvaluacionForm.preguntas[i];

            if (!pregunta.Pregunta_Texto.trim()) {
                setEditInfoMessage(`La pregunta ${i + 1} debe tener texto.`);
                return false;
            }

            if (
                pregunta.Pregunta_Orden === "" ||
                pregunta.Pregunta_Orden === null ||
                Number(pregunta.Pregunta_Orden) < 1
            ) {
                setEditInfoMessage(`La pregunta ${i + 1} debe tener un orden válido.`);
                return false;
            }

            if (
                pregunta.Pregunta_Puntos_Estreno === "" ||
                pregunta.Pregunta_Puntos_Estreno === null ||
                Number(pregunta.Pregunta_Puntos_Estreno) < 0
            ) {
                setEditInfoMessage(
                    `La pregunta ${i + 1} debe tener puntos de estreno válidos.`
                );
                return false;
            }

            if (
                pregunta.Pregunta_Puntos_Normal === "" ||
                pregunta.Pregunta_Puntos_Normal === null ||
                Number(pregunta.Pregunta_Puntos_Normal) < 0
            ) {
                setEditInfoMessage(
                    `La pregunta ${i + 1} debe tener puntos normales válidos.`
                );
                return false;
            }

            if (!Array.isArray(pregunta.respuestas) || pregunta.respuestas.length !== 4) {
                setEditInfoMessage(
                    `La pregunta ${i + 1} debe tener exactamente 4 respuestas.`
                );
                return false;
            }

            const correctas = pregunta.respuestas.filter(
                (respuesta) => Number(respuesta.Respuesta_Correcta) === 1
            );

            if (correctas.length !== 1) {
                setEditInfoMessage(
                    `La pregunta ${i + 1} debe tener una sola respuesta correcta.`
                );
                return false;
            }

            for (let j = 0; j < pregunta.respuestas.length; j += 1) {
                if (!pregunta.respuestas[j].Respuesta_Texto.trim()) {
                    setEditInfoMessage(
                        `La respuesta ${j + 1} de la pregunta ${i + 1} no puede estar vacía.`
                    );
                    return false;
                }
            }
        }

        return true;
    };

    const validateEditVideoForm = () => {
        if (!editVideoForm.Video_ID) {
            setEditErrorMessage("No se encontró el video a editar.");
            return false;
        }

        if (!editVideoForm.Video_Titulo.trim()) {
            setEditInfoMessage("El campo título es obligatorio.");
            return false;
        }

        if (!editVideoForm.Instructor_ID) {
            setEditInfoMessage("El campo experto es obligatorio.");
            return false;
        }

        if (
            !editVideoForm.Video_Descripcion ||
            !editVideoForm.Video_Descripcion.trim() ||
            editVideoForm.Video_Descripcion === "<p><br></p>"
        ) {
            setEditInfoMessage("El campo descripción es obligatorio.");
            return false;
        }

        if (!editVideoForm.Video_URL_Vimeo.trim()) {
            setEditInfoMessage("El campo ID Vimeo es obligatorio.");
            return false;
        }

        if (!editVideoForm.Video_Duracion_Seg.trim()) {
            setEditInfoMessage("El campo duración aproximada es obligatorio.");
            return false;
        }

        if (!/^\d+$/.test(editVideoForm.Video_Duracion_Seg.trim())) {
            setEditInfoMessage("La duración aproximada debe ser un número entero.");
            return false;
        }

        if (!validateEditEvaluacionForm()) {
            return false;
        }

        if (Number(editVideoForm.Video_Tiene_Recursos) === 1) {
            if (!Array.isArray(editVideoResources) || editVideoResources.length === 0) {
                setEditInfoMessage("Debes agregar al menos un recurso si el video tendrá recursos.");
                return false;
            }

            for (const resource of editVideoResources) {
                if (!String(resource.Recurso_Titulo || "").trim()) {
                    setEditInfoMessage("Todos los recursos deben tener título.");
                    return false;
                }

                if (resource.Recurso_Tipo === "link") {
                    if (!String(resource.Recurso_URL || "").trim()) {
                        setEditInfoMessage(`El recurso "${resource.Recurso_Titulo}" debe tener una URL.`);
                        return false;
                    }
                }

                if (resource.Recurso_Tipo === "archivo") {
                    if (!resource.resourceFile && !resource.Recurso_URL) {
                        setEditInfoMessage(`El recurso "${resource.Recurso_Titulo}" debe tener un archivo.`);
                        return false;
                    }
                }
            }
        }

        return true;
    };

    const createEmptyEditResource = (order = 1) => ({
        tempId: `edit_resource_${Date.now()}_${Math.random().toString(16).slice(2)}`,
        Recurso_ID: null,
        Recurso_Titulo: "",
        Recurso_Tipo: "link",
        Recurso_URL: "",
        Recurso_Imagen: "",
        Recurso_Orden: order,
        resourceImageFile: null,
        resourceFile: null,
        Recurso_Nombre_Archivo: "",
        Current_Recurso_Imagen: "",
    });

    const addEditResource = () => {
        setEditVideoResources((prev) => [
            ...prev,
            createEmptyEditResource(prev.length + 1),
        ]);
    };

    const removeEditResource = (tempId) => {
        setEditVideoResources((prev) =>
            prev
                .filter((item) => item.tempId !== tempId)
                .map((item, index) => ({
                    ...item,
                    Recurso_Orden: index + 1,
                }))
        );
    };

    const handleEditResourceChange = (tempId, field, value) => {
        setEditVideoResources((prev) =>
            prev.map((item) =>
                item.tempId === tempId
                    ? {
                        ...item,
                        [field]: value,
                    }
                    : item
            )
        );
    };

    const handleEditResourceTypeChange = (tempId, tipo) => {
        setEditVideoResources((prev) =>
            prev.map((item) =>
                item.tempId === tempId
                    ? {
                        ...item,
                        Recurso_Tipo: tipo,
                        Recurso_URL: tipo === "link" ? item.Recurso_URL : item.Recurso_URL || "",
                        resourceFile: tipo === "archivo" ? item.resourceFile : null,
                        Recurso_Nombre_Archivo:
                            tipo === "archivo" ? item.Recurso_Nombre_Archivo : "",
                    }
                    : item
            )
        );
    };

    const handleEditResourceImageFileChange = (tempId, file) => {
        setEditVideoResources((prev) =>
            prev.map((item) =>
                item.tempId === tempId
                    ? {
                        ...item,
                        resourceImageFile: file || null,
                    }
                    : item
            )
        );
    };

    const handleEditResourceFileChange = (tempId, file) => {
        setEditVideoResources((prev) =>
            prev.map((item) =>
                item.tempId === tempId
                    ? {
                        ...item,
                        resourceFile: file || null,
                        Recurso_Nombre_Archivo: file?.name || item.Recurso_Nombre_Archivo || "",
                    }
                    : item
            )
        );
    };

    const handleSubmitEditVideo = async (e) => {
        e.preventDefault();
        resetEditMessages();

        const isValid = validateEditVideoForm();
        if (!isValid) return;

        try {
            setSavingEditVideo(true);

            const formData = new FormData();
            formData.append("Video_Titulo", String(editVideoForm.Video_Titulo || "").trim());
            formData.append("Instructor_ID", editVideoForm.Instructor_ID);
            formData.append("Video_Descripcion", editVideoForm.Video_Descripcion || "");
            formData.append("Video_URL_Vimeo", String(editVideoForm.Video_URL_Vimeo || "").trim());
            formData.append("Video_Duracion_Seg", String(editVideoForm.Video_Duracion_Seg || "").trim());
            formData.append(
                "Video_Tiene_Evaluacion",
                Number(editVideoForm.Video_Tiene_Evaluacion) === 1 ? 1 : 0
            );
            formData.append(
                "Video_Tiene_Recursos",
                Number(editVideoForm.Video_Tiene_Recursos) === 1 ? 1 : 0
            );
            formData.append("tags", JSON.stringify(editSelectedTags));

            if (editVideoForm.Video_Miniatura) {
                formData.append("Video_Miniatura", editVideoForm.Video_Miniatura);
            }

            if (editVideoForm.Video_Miniatura_Sitio) {
                formData.append("Video_Miniatura_Sitio", editVideoForm.Video_Miniatura_Sitio);
            }

            if (Number(editVideoForm.Video_Tiene_Evaluacion) === 1) {
                formData.append("evaluacion", JSON.stringify(editEvaluacionForm));
            }

            if (Number(editVideoForm.Video_Tiene_Recursos) === 1) {
                formData.append(
                    "resources",
                    JSON.stringify(
                        editVideoResources.map((resource, index) => ({
                            tempId: resource.tempId,
                            Recurso_ID: resource.Recurso_ID || null,
                            Recurso_Titulo: String(resource.Recurso_Titulo || "").trim(),
                            Recurso_Tipo: resource.Recurso_Tipo,
                            Recurso_URL: String(resource.Recurso_URL || "").trim(),
                            Recurso_Imagen: resource.Recurso_Imagen || "",
                            Recurso_Orden: Number(resource.Recurso_Orden || index + 1),
                            Recurso_Nombre_Archivo: resource.Recurso_Nombre_Archivo || "",
                        }))
                    )
                );

                editVideoResources.forEach((resource) => {
                    if (resource.resourceImageFile) {
                        formData.append(
                            `resourceImage_${resource.tempId}`,
                            resource.resourceImageFile
                        );
                    }

                    if (resource.Recurso_Tipo === "archivo" && resource.resourceFile) {
                        formData.append(
                            `resourceFile_${resource.tempId}`,
                            resource.resourceFile
                        );
                    }
                });
            }

            const response = await fetch(
                `${API_BASE}/api/admin/videoList/${editVideoForm.Video_ID}`,
                {
                    method: "PUT",
                    credentials: "include",
                    body: formData,
                }
            );

            const data = await response.json();

            if (!response.ok) {
                if (response.status === 400 || response.status === 404) {
                    setEditInfoMessage(
                        data?.message || "Verifica la información del formulario."
                    );
                    return;
                }

                throw new Error(data?.message || "No fue posible actualizar el video");
            }

            await loadVideos();

            setEditSuccessMessage(data?.message || "El video se actualizó con éxito.");
        } catch (error) {
            console.error("Error al actualizar video:", error);
            setEditErrorMessage(
                error.message || "Ocurrió un error al actualizar el video"
            );
        } finally {
            setSavingEditVideo(false);
        }
    };

    // Funcion para Desactivar video
    const handleDeleteVideo = async (videoId) => {
        if (!videoId) return;

        const result = await Swal.fire({
            title: "¿Desactivar video?",
            text: "Esta acción es irreversible y el video dejará de aparecer como activo.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#2f57ef",
            cancelButtonColor: "#d33",
            confirmButtonText: "Sí, desactivar",
            cancelButtonText: "Cancelar",
            reverseButtons: true,
            focusCancel: true,
        });

        if (!result.isConfirmed) return;

        try {
            const response = await fetch(
                `${API_BASE}/api/admin/videoList/${videoId}`,
                {
                    method: "DELETE",
                    credentials: "include",
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data?.message || "No fue posible desactivar el video");
            }

            await loadVideos();

            if (selectedVideo?.Video_ID === videoId) {
                setSelectedVideo(null);
            }

            await Swal.fire({
                title: "Correcto",
                text: data?.message || "El video fue desactivado correctamente.",
                icon: "success",
                confirmButtonText: "Aceptar",
            });

        } catch (error) {
            console.error("Error al desactivar video:", error);

            await Swal.fire({
                title: "Error",
                text: error.message || "Ocurrió un error al desactivar el video.",
                icon: "error",
                confirmButtonText: "Aceptar",
            });
        }
    };

    // Helpers

    const renderTagsBadges = (tags = []) => {
        if (!Array.isArray(tags) || tags.length === 0) {
            return <span className="text-muted">Sin tags</span>;
        }

        return (
            <div className="d-flex flex-wrap gap-2">
                {tags.map((tag) => (
                    <span
                        key={tag.Tag_ID || tag.VideoTagRel_ID}
                        className="badge"
                        style={{
                            backgroundColor: tag.Tag_Color || "#e9ecef",
                            color: "#212529",
                            padding: "0.5rem 0.75rem",
                        }}
                    >
                        {tag.Tag_Nombre}
                    </span>
                ))}
            </div>
        );
    };

    const createEmptyResource = (order = 1) => ({
        tempId: `resource_${Date.now()}_${Math.random().toString(16).slice(2)}`,
        Recurso_ID: null,
        Recurso_Titulo: "",
        Recurso_Tipo: "link",
        Recurso_URL: "",
        Recurso_Imagen: "",
        Recurso_Orden: order,
        resourceImageFile: null,
        resourceFile: null,
        Recurso_Nombre_Archivo: "",
    });

    // Filtros

    const normalizeText = (text = "") => {
        return String(text)
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .trim();
    };

    const filteredVideos = useMemo(() => {
        const search = normalizeText(videoSearchTerm);

        let result = Array.isArray(videos) ? [...videos] : [];

        if (search) {
            result = result.filter((video) => {
                const title = normalizeText(video.Video_Titulo);
                const instructor = normalizeText(video.Instructor_Nombre);

                return title.includes(search) || instructor.includes(search);
            });
        }

        result.sort((a, b) => {
            if (videoSortOrder === "default") {
                return Number(a.Video_ID || 0) - Number(b.Video_ID || 0);
            }

            if (videoSortOrder === "az") {
                return String(a.Video_Titulo || "").localeCompare(
                    String(b.Video_Titulo || ""),
                    "es",
                    { sensitivity: "base" }
                );
            }

            if (videoSortOrder === "za") {
                return String(b.Video_Titulo || "").localeCompare(
                    String(a.Video_Titulo || ""),
                    "es",
                    { sensitivity: "base" }
                );
            }

            if (videoSortOrder === "oldest") {
                return Number(a.Video_ID || 0) - Number(b.Video_ID || 0);
            }

            if (videoSortOrder === "newest") {
                return Number(b.Video_ID || 0) - Number(a.Video_ID || 0);
            }

            return 0;
        });

        return result;
    }, [videos, videoSearchTerm, videoSortOrder]);

    // Paginador
    const {
        currentPage,
        totalPages,
        totalItems,
        paginatedItems,
        visiblePages,
        startItem,
        endItem,
        goToPage,
        goToPreviousPage,
        goToNextPage,
        resetPagination,
        hasPreviousPage,
        hasNextPage,
    } = usePagination(filteredVideos, 8, 10);

    const handleClearFilters = () => {
        setVideoSearchTerm("");
        setVideoSortOrder("default");
        resetPagination();
    };

    useEffect(() => {
        resetPagination();
    }, [videoSearchTerm, videoSortOrder]);

    return (
        <>
        <Sidebar/>
        <main class="main-content">
            <Header/>
            <div class="content-inner container-fluid pb-0" id="page_layout">
                <div class="d-flex justify-content-between align-items-center flex-wrap mb-4 gap-3">
                    <div class="d-flex flex-column">
                        <h3>Lista de Videos</h3>
                        <p class="text-primary mb-0">Panduit Core</p>
                    </div>
                </div>

                <div class="row">
                    <div class="col-sm-12">
                        <div class="card">
                            <div class="card-header d-flex justify-content-end gap-3 flex-wrap">
                                <div className="row g-2 align-items-end w-100">
                                <div className="col-md-6">
                                    <label className="form-label mb-1">Buscar</label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder="Buscar por video o experto..."
                                        value={videoSearchTerm}
                                        onChange={(e) => setVideoSearchTerm(e.target.value)}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <label className="form-label mb-1">Ordenar por</label>
                                    <select
                                        className="form-select form-select-sm"
                                        value={videoSortOrder}
                                        onChange={(e) => setVideoSortOrder(e.target.value)}
                                    >
                                        <option value="default">Default</option>
                                        <option value="az">Orden alfabético A-Z</option>
                                        <option value="za">Orden alfabético Z-A</option>
                                        <option value="oldest">Más antiguos</option>
                                        <option value="newest">Más recientes</option>
                                    </select>
                                </div>

                                <div className="col-md-3 d-flex justify-content-end gap-2 ms-auto">
                                    <button
                                        type="button"
                                        className="btn btn-secondary-subtle btn-sm"
                                        onClick={handleClearFilters}
                                        title="Limpiar búsqueda"
                                    >
                                        <svg width="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <g opacity="0.4">
                                            <path
                                                d="M4.88076 14.6713C4.74978 14.2784 4.32504 14.066 3.93208 14.197C3.53912 14.328 3.32675 14.7527 3.45774 15.1457L4.88076 14.6713ZM20.8808 15.1457C21.0117 14.7527 20.7994 14.328 20.4064 14.197C20.0135 14.066 19.5887 14.2784 19.4577 14.6713L20.8808 15.1457ZM4.16925 14.9085C3.45774 15.1457 3.45785 15.146 3.45797 15.1464C3.45802 15.1465 3.45815 15.1469 3.45825 15.1472C3.45845 15.1478 3.45868 15.1485 3.45895 15.1493C3.45948 15.1509 3.46013 15.1528 3.46092 15.1551C3.46249 15.1597 3.46456 15.1657 3.46716 15.1731C3.47235 15.188 3.47961 15.2084 3.48902 15.2341C3.50782 15.2854 3.53521 15.3576 3.5717 15.4477C3.64461 15.6279 3.7542 15.8805 3.90468 16.1814C4.2048 16.7817 4.67223 17.5836 5.34308 18.3886C6.68942 20.0043 8.88343 21.6585 12.1693 21.6585V20.1585C9.45507 20.1585 7.64908 18.8128 6.49542 17.4284C5.91627 16.7334 5.5087 16.0354 5.24632 15.5106C5.11555 15.2491 5.02201 15.0329 4.96212 14.8849C4.9322 14.811 4.91076 14.7543 4.89733 14.7177C4.89062 14.6994 4.88593 14.6861 4.88318 14.6783C4.88181 14.6744 4.88093 14.6718 4.88053 14.6706C4.88033 14.67 4.88025 14.6698 4.88029 14.6699C4.88031 14.67 4.88036 14.6701 4.88044 14.6704C4.88047 14.6705 4.88056 14.6707 4.88058 14.6708C4.88067 14.671 4.88076 14.6713 4.16925 14.9085ZM12.1693 21.6585C15.4551 21.6585 17.6491 20.0043 18.9954 18.3886C19.6663 17.5836 20.1337 16.7817 20.4338 16.1814C20.5843 15.8805 20.6939 15.6279 20.7668 15.4477C20.8033 15.3576 20.8307 15.2854 20.8495 15.2341C20.8589 15.2084 20.8662 15.188 20.8713 15.1731C20.8739 15.1657 20.876 15.1597 20.8776 15.1551C20.8784 15.1528 20.879 15.1509 20.8796 15.1493C20.8798 15.1485 20.8801 15.1478 20.8803 15.1472C20.8804 15.1469 20.8805 15.1465 20.8805 15.1464C20.8807 15.146 20.8808 15.1457 20.1693 14.9085C19.4577 14.6713 19.4578 14.671 19.4579 14.6708C19.4579 14.6707 19.458 14.6705 19.4581 14.6704C19.4581 14.6701 19.4582 14.67 19.4582 14.6699C19.4583 14.6698 19.4582 14.67 19.458 14.6706C19.4576 14.6718 19.4567 14.6744 19.4553 14.6783C19.4526 14.6861 19.4479 14.6994 19.4412 14.7177C19.4277 14.7543 19.4063 14.811 19.3764 14.8849C19.3165 15.0329 19.223 15.2491 19.0922 15.5106C18.8298 16.0354 18.4222 16.7334 17.8431 17.4284C16.6894 18.8128 14.8834 20.1585 12.1693 20.1585V21.6585Z"
                                                fill="currentColor"></path>
                                            <path d="M21.5183 19.2271C21.4293 19.2234 21.3427 19.196 21.2671 19.1465L16.3546 15.8924C16.2197 15.8026 16.1413 15.6537 16.148 15.4969C16.1546 15.34 16.2452 15.1982 16.3873 15.1202L21.5571 12.2926C21.7075 12.2106 21.8932 12.213 22.0416 12.3003C22.1907 12.387 22.2783 12.5436 22.2712 12.7096L22.014 18.7913C22.007 18.9573 21.9065 19.1059 21.7506 19.1797C21.6772 19.215 21.597 19.2305 21.5183 19.2271" fill="currentColor"></path>
                                            </g>
                                            <path
                                            d="M20.0742 10.0265C20.1886 10.4246 20.6041 10.6546 21.0022 10.5401C21.4003 10.4257 21.6302 10.0102 21.5158 9.61214L20.0742 10.0265ZM4.10803 8.88317C3.96071 9.27031 4.15513 9.70356 4.54226 9.85087C4.92939 9.99818 5.36265 9.80377 5.50996 9.41664L4.10803 8.88317ZM20.795 9.81934C21.5158 9.61214 21.5157 9.6118 21.5156 9.61144C21.5155 9.61129 21.5154 9.6109 21.5153 9.61059C21.5152 9.60998 21.515 9.60928 21.5147 9.60848C21.5143 9.60689 21.5137 9.60493 21.513 9.6026C21.5116 9.59795 21.5098 9.59184 21.5075 9.58431C21.503 9.56925 21.4966 9.54853 21.4882 9.52251C21.4716 9.47048 21.4473 9.39719 21.4146 9.3056C21.3493 9.12256 21.2503 8.8656 21.1126 8.55861C20.8378 7.94634 20.4044 7.12552 19.7678 6.29313C18.4902 4.62261 16.3673 2.87801 13.0844 2.74053L13.0216 4.23922C15.7334 4.35278 17.4816 5.77291 18.5763 7.20436C19.1258 7.92295 19.5038 8.63743 19.744 9.17271C19.8638 9.43949 19.9482 9.65937 20.0018 9.80972C20.0286 9.88483 20.0477 9.94238 20.0596 9.97951C20.0655 9.99808 20.0696 10.0115 20.072 10.0195C20.0732 10.0235 20.074 10.0261 20.0744 10.0273C20.0746 10.0278 20.0746 10.0281 20.0746 10.028C20.0746 10.0279 20.0745 10.0278 20.0745 10.0275C20.0744 10.0274 20.0744 10.0272 20.0743 10.0271C20.0743 10.0268 20.0742 10.0265 20.795 9.81934ZM13.0844 2.74053C9.80146 2.60306 7.54016 4.16407 6.12741 5.72193C5.42345 6.49818 4.92288 7.27989 4.59791 7.86704C4.43497 8.16144 4.31491 8.40923 4.23452 8.58617C4.1943 8.67471 4.16391 8.7457 4.14298 8.79616C4.13251 8.82139 4.1244 8.84151 4.11859 8.85613C4.11568 8.86344 4.11336 8.86938 4.1116 8.8739C4.11072 8.87616 4.10998 8.87807 4.10939 8.87962C4.10909 8.88039 4.10883 8.88108 4.1086 8.88167C4.10849 8.88196 4.10834 8.88234 4.10829 8.88249C4.10815 8.88284 4.10803 8.88317 4.80899 9.14991C5.50996 9.41664 5.50985 9.41692 5.50975 9.41719C5.50973 9.41725 5.50964 9.41749 5.50959 9.4176C5.5095 9.41784 5.50945 9.41798 5.50942 9.41804C5.50938 9.41816 5.50947 9.41792 5.50969 9.41734C5.51014 9.41619 5.51113 9.41365 5.51267 9.40979C5.51574 9.40206 5.52099 9.38901 5.52846 9.37101C5.5434 9.335 5.56719 9.27924 5.60018 9.20664C5.66621 9.0613 5.76871 8.84925 5.91031 8.59341C6.19442 8.08008 6.63084 7.39971 7.23855 6.72958C8.44912 5.39466 10.3098 4.12566 13.0216 4.23922L13.0844 2.74053Z"
                                            fill="currentColor"></path>
                                            <path d="M8.78337 9.33604C8.72981 9.40713 8.65805 9.46292 8.57443 9.49703L3.1072 11.6951C2.95672 11.7552 2.78966 11.7352 2.66427 11.6407C2.53887 11.5462 2.47359 11.3912 2.48993 11.2299L3.09576 5.36863C3.11367 5.19823 3.22102 5.04666 3.37711 4.97402C3.5331 4.9005 3.71173 4.91728 3.84442 5.01726L8.70581 8.68052C8.8385 8.78051 8.90387 8.94759 8.8762 9.1178C8.86358 9.19825 8.83082 9.27308 8.78337 9.33604" fill="currentColor"></path>
                                        </svg> 
                                    </button>

                                    <button class="btn btn-primary btn-sm" data-bs-toggle="modal" data-bs-target="#addVideoModal">
                                        <span class="btn-inner mr-5">
                                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon-24"><path d="M12 4V20M20 12H4" stroke="currentColor" strokeWidth="2" stroke-linecap="round" stroke-linejoin="round"></path></svg>
                                        </span>
                                        Agregar Video
                                    </button>
                                </div>

                                </div>
                            </div>

                            <div class="card-body px-0">
                                <div class="table-responsive">
                                    <table id="user-list-table" class="table table-striped" role="grid" data-toggle="data-table">
                                        <thead>
                                            <tr class="ligth">
                                            <th>ID</th>
                                            <th>Nombre</th>
                                            <th>Experto</th>
                                            <th>Estado</th>
                                            <th>Acción</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loadingVideos ? (
                                                <tr>
                                                    <td colSpan="10" className="text-center py-4">
                                                        <div className="d-flex flex-column align-items-center justify-content-center gap-2">
                                                            <div className="col-md-12 col-sm-4 mb-4">
                                                                <div className="iq-loader-box">
                                                                    <div className="iq-loader-1"></div>
                                                                </div>
                                                            </div>

                                                            <span className="text-muted">
                                                                Cargando videos...
                                                            </span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : paginatedItems.length === 0 ? (
                                                <tr>
                                                    <td colSpan="5" className="text-center py-4">
                                                        No hay videos registrados.
                                                    </td>
                                                </tr>
                                            ) : (
                                                paginatedItems.map((video) => (
                                                    <tr key={video.Video_ID}>
                                                        <td>{video.Video_ID}</td>

                                                        <td>{video.Video_Titulo}</td>

                                                        <td>
                                                            <div className="d-flex justify-content-start align-items-center">
                                                                <div className="pe-3">
                                                                    <img
                                                                        src={video.Instructor_Foto || "/assets/images/avatars/01.png"}
                                                                        className="rounded-circle bg-primary-subtle p-1 me-3"
                                                                        width="50"
                                                                        height="50"
                                                                        alt={video.Instructor_Nombre || "Instructor"}
                                                                        loading="lazy"
                                                                        style={{ objectFit: "cover" }}
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <h6 className="mb-1">{video.Instructor_Nombre}</h6>
                                                                </div>
                                                            </div>
                                                        </td>

                                                        <td>{renderVideoStatus(video.Video_Activo)}</td>

                                                        <td>
                                                            <div className="d-flex">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-icon btn-info-subtle ms-2"
                                                                    id="viewVideoModalLabel"
                                                                    data-bs-toggle="modal"
                                                                    data-bs-target="#viewVideoModal"
                                                                    onClick={() => {
                                                                        handleOpenViewVideo(video); 
                                                                    }}
                                                                >
                                                                    <span className="btn-inner">
                                                                        <svg className="icon-32" width="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                            <path opacity="0.4" fillRule="evenodd" clipRule="evenodd" d="M17.7366 6.04606C19.4439 7.36388 20.8976 9.29455 21.9415 11.7091C22.0195 11.8924 22.0195 12.1067 21.9415 12.2812C19.8537 17.1103 16.1366 20 12 20H11.9902C7.86341 20 4.14634 17.1103 2.05854 12.2812C1.98049 12.1067 1.98049 11.8924 2.05854 11.7091C4.14634 6.87903 7.86341 4 11.9902 4H12C14.0683 4 16.0293 4.71758 17.7366 6.04606ZM8.09756 12C8.09756 14.1333 9.8439 15.8691 12 15.8691C14.1463 15.8691 15.8927 14.1333 15.8927 12C15.8927 9.85697 14.1463 8.12121 12 8.12121C9.8439 8.12121 8.09756 9.85697 8.09756 12Z" fill="currentColor"></path>
                                                                            <path d="M14.4308 11.997C14.4308 13.3255 13.3381 14.4115 12.0015 14.4115C10.6552 14.4115 9.5625 13.3255 9.5625 11.997C9.5625 11.8321 9.58201 11.678 9.61128 11.5228H9.66006C10.743 11.5228 11.621 10.6695 11.6601 9.60184C11.7674 9.58342 11.8845 9.57275 12.0015 9.57275C13.3381 9.57275 14.4308 10.6588 14.4308 11.997Z" fill="currentColor"></path>
                                                                        </svg>
                                                                    </span>
                                                                </button>

                                                                <button
                                                                    type="button"
                                                                    className="btn btn-icon btn-warning-subtle ms-2"
                                                                    id="editVideoModalLabel"
                                                                    data-bs-toggle="modal"
                                                                    data-bs-target="#editVideoModal"
                                                                    onClick={() => {
                                                                        // aquí luego colocas tu función para editar
                                                                        handleOpenEditVideo(video);
                                                                    }}
                                                                >
                                                                    <span className="btn-inner">
                                                                        <svg
                                                                            className="icon-32"
                                                                            width="32"
                                                                            viewBox="0 0 24 24"
                                                                            fill="none"
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                        >
                                                                            <path
                                                                                opacity="0.4"
                                                                                d="M19.9927 18.9534H14.2984C13.7429 18.9534 13.291 19.4124 13.291 19.9767C13.291 20.5422 13.7429 21.0001 14.2984 21.0001H19.9927C20.5483 21.0001 21.0001 20.5422 21.0001 19.9767C21.0001 19.4124 20.5483 18.9534 19.9927 18.9534Z"
                                                                                fill="currentColor"
                                                                            ></path>
                                                                            <path
                                                                                d="M10.309 6.90385L15.7049 11.2639C15.835 11.3682 15.8573 11.5596 15.7557 11.6929L9.35874 20.0282C8.95662 20.5431 8.36402 20.8344 7.72908 20.8452L4.23696 20.8882C4.05071 20.8903 3.88775 20.7613 3.84542 20.5764L3.05175 17.1258C2.91419 16.4915 3.05175 15.8358 3.45388 15.3306L9.88256 6.95545C9.98627 6.82108 10.1778 6.79743 10.309 6.90385Z"
                                                                                fill="currentColor"
                                                                            ></path>
                                                                            <path
                                                                                opacity="0.4"
                                                                                d="M18.1208 8.66544L17.0806 9.96401C16.9758 10.0962 16.7874 10.1177 16.6573 10.0124C15.3927 8.98901 12.1545 6.36285 11.2561 5.63509C11.1249 5.52759 11.1069 5.33625 11.2127 5.20295L12.2159 3.95706C13.126 2.78534 14.7133 2.67784 15.9938 3.69906L17.4647 4.87078C18.0679 5.34377 18.47 5.96726 18.6076 6.62299C18.7663 7.3443 18.597 8.0527 18.1208 8.66544Z"
                                                                                fill="currentColor"
                                                                            ></path>
                                                                        </svg>
                                                                    </span>
                                                                </button>

                                                                <button
                                                                    type="button"
                                                                    className="btn btn-icon btn-danger-subtle ms-2"
                                                                    onClick={() => handleDeleteVideo(video.Video_ID)}
                                                                >
                                                                    <span className="btn-inner">
                                                                        <svg
                                                                            className="icon-32"
                                                                            width="32"
                                                                            viewBox="0 0 24 24"
                                                                            fill="none"
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                        >
                                                                            <path
                                                                                opacity="0.4"
                                                                                d="M19.643 9.48851C19.643 9.5565 19.11 16.2973 18.8056 19.1342C18.615 20.8751 17.4927 21.9311 15.8092 21.9611C14.5157 21.9901 13.2494 22.0001 12.0036 22.0001C10.6809 22.0001 9.38741 21.9901 8.13185 21.9611C6.50477 21.9221 5.38147 20.8451 5.20057 19.1342C4.88741 16.2873 4.36418 9.5565 4.35445 9.48851C4.34473 9.28351 4.41086 9.08852 4.54507 8.93053C4.67734 8.78453 4.86796 8.69653 5.06831 8.69653H18.9388C19.1382 8.69653 19.3191 8.78453 19.4621 8.93053C19.5953 9.08852 19.6624 9.28351 19.643 9.48851Z"
                                                                                fill="currentColor"
                                                                            ></path>
                                                                            <path
                                                                                d="M21 5.97686C21 5.56588 20.6761 5.24389 20.2871 5.24389H17.3714C16.7781 5.24389 16.2627 4.8219 16.1304 4.22692L15.967 3.49795C15.7385 2.61698 14.9498 2 14.0647 2H9.93624C9.0415 2 8.26054 2.61698 8.02323 3.54595L7.87054 4.22792C7.7373 4.8219 7.22185 5.24389 6.62957 5.24389H3.71385C3.32386 5.24389 3 5.56588 3 5.97686V6.35685C3 6.75783 3.32386 7.08982 3.71385 7.08982H20.2871C20.6761 7.08982 21 6.75783 21 6.35685V5.97686Z"
                                                                                fill="currentColor"
                                                                            ></path>
                                                                        </svg>
                                                                    </span>
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <UniversalPagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                totalItems={totalItems}
                                startItem={startItem}
                                endItem={endItem}
                                visiblePages={visiblePages}
                                onPageChange={goToPage}
                                onPrevious={goToPreviousPage}
                                onNext={goToNextPage}
                                hasPreviousPage={hasPreviousPage}
                                hasNextPage={hasNextPage}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </main>

        {/* Modal para agregar Video */}
        <div
            className="modal fade"
            id="addVideoModal"
            tabIndex="-1"
            aria-labelledby="addVideoModalLabel"
            aria-hidden="true"
        >
            <div className="modal-dialog modal-dialog-centered modal-xl">
                <div className="modal-content">
                    <form onSubmit={handleSubmitVideo}>
                        <div className="modal-header">
                            <h5 className="modal-title" id="addVideoModalLabel">
                                Agregar Video
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                                onClick={() => {
                                    resetMessages();
                                    resetVideoForm();
                                }}
                            ></button>
                        </div>

                        <div className="modal-body">
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <label className="form-label" htmlFor="titulo">
                                        Título: <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        id="titulo"
                                        name="Video_Titulo"
                                        type="text"
                                        className="form-control"
                                        value={videoForm.Video_Titulo}
                                        onChange={handleVideoInputChange}
                                        disabled={savingVideo}
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label" htmlFor="experto">
                                        Experto: <span className="text-danger">*</span>
                                    </label>
                                    <select
                                        id="experto"
                                        name="Instructor_ID"
                                        className="form-select"
                                        value={videoForm.Instructor_ID}
                                        onChange={handleVideoInputChange}
                                        disabled={savingVideo || loadingInstructors}
                                    >
                                        <option value="">
                                            {loadingInstructors
                                                ? "Cargando expertos..."
                                                : "Selecciona un experto"}
                                        </option>

                                        {instructors.map((instructor) => (
                                            <option
                                                key={instructor.Instructor_ID}
                                                value={instructor.Instructor_ID}
                                            >
                                                {instructor.Instructor_Nombre}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="row mb-3">
                                <div className="col">
                                    <label className="form-label">
                                        Descripción: <span className="text-danger">*</span>
                                    </label>

                                    <div className="border rounded video-quill-wrapper">
                                        <ReactQuill
                                            theme="snow"
                                            value={videoForm.Video_Descripcion}
                                            onChange={handleDescriptionChange}
                                            placeholder="Escribe la descripción del video..."
                                            readOnly={savingVideo}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <label className="form-label" htmlFor="customFileVideo">
                                        Selecciona una miniatura: 
                                    </label>
                                    <input
                                        className="form-control"
                                        type="file"
                                        id="customFileVideo"
                                        name="Video_Miniatura"
                                        accept=".jpg,.jpeg,.png,.webp"
                                        onChange={handleVideoInputChange}
                                        disabled={savingVideo}
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label" htmlFor="customFileVideoSite">
                                        Selecciona una miniatura para el sitio: 
                                    </label>
                                    <input
                                        className="form-control"
                                        type="file"
                                        id="customFileVideoSite"
                                        name="Video_Miniatura_Sitio"
                                        accept=".jpg,.jpeg,.png,.webp"
                                        onChange={handleVideoInputChange}
                                        disabled={savingVideo}
                                    />
                                </div>
                            </div>

                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <label className="form-label" htmlFor="vimeo">
                                        ID Vimeo: <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        id="vimeo"
                                        name="Video_URL_Vimeo"
                                        type="text"
                                        className="form-control"
                                        value={videoForm.Video_URL_Vimeo}
                                        onChange={handleVideoInputChange}
                                        disabled={savingVideo}
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label" htmlFor="duracion_aprox">
                                        Duración Aprox: <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        id="duracion_aprox"
                                        name="Video_Duracion_Seg"
                                        type="text"
                                        className="form-control"
                                        value={videoForm.Video_Duracion_Seg}
                                        onChange={handleVideoInputChange}
                                        disabled={savingVideo}
                                    />
                                </div>
                            </div>

                            <div className="row mb-4">
                                <div className="col-md-12">
                                    <label className="form-label">
                                        Tags del video
                                    </label>

                                    <div className="border rounded p-3 bg-light-subtle">
                                        {loadingTags ? (
                                            <p className="mb-0 text-muted">Cargando tags...</p>
                                        ) : tagsCatalog.length === 0 ? (
                                            <p className="mb-0 text-muted">No hay tags disponibles.</p>
                                        ) : (
                                            <div className="row">
                                                {tagsCatalog.map((tag) => (
                                                    <div className="col-md-4 mb-2" key={tag.Tag_ID}>
                                                        <div className="form-check">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                id={`tag-add-${tag.Tag_ID}`}
                                                                checked={selectedTags.includes(Number(tag.Tag_ID))}
                                                                onChange={() => handleTagToggle(Number(tag.Tag_ID))}
                                                                disabled={savingVideo}
                                                            />
                                                            <label
                                                                className="form-check-label"
                                                                htmlFor={`tag-add-${tag.Tag_ID}`}
                                                            >
                                                                <span
                                                                    className="badge me-2"
                                                                    style={{
                                                                        backgroundColor: tag.Tag_Color || "#e9ecef",
                                                                        color: "#212529",
                                                                    }}
                                                                >
                                                                    {tag.Tag_Tipo}
                                                                </span>
                                                                {tag.Tag_Nombre}
                                                            </label>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* ¿Este video tiene evaluacion? */}
                            <div className="row mb-4">
                                <div className="col-md-12">
                                    <div className="mb-3 form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="videoEvaluacion"
                                            name="Video_Tiene_Evaluacion"
                                            checked={videoForm.Video_Tiene_Evaluacion === 1}
                                            onChange={handleVideoInputChange}
                                            disabled={savingVideo}
                                        />
                                        <label className="form-check-label" htmlFor="videoEvaluacion">
                                            ¿Este video tendrá evaluación?
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {Number(videoForm.Video_Tiene_Evaluacion) === 1 && (
                                <div className="border rounded p-3 mb-4 bg-light-subtle">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5 className="mb-0">Configuración de evaluación</h5>
                                        <div className="d-flex gap-2">
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary btn-sm"
                                                onClick={() => setShowEvaluationConfig((prev) => !prev)}
                                                disabled={savingVideo}
                                            >
                                                {showEvaluationConfig ? "Ocultar" : "Mostrar"}
                                            </button>


                                            <button
                                                type="button"
                                                className="btn btn-primary btn-sm"
                                                onClick={addPregunta}
                                                disabled={savingVideo}
                                            >
                                                Agregar pregunta
                                            </button>
                                        </div>
                                    </div>

                                    {showEvaluationConfig && (
                                    <>
                                        <div className="row mb-3">
                                            <div className="col-md-6">
                                                <label className="form-label">
                                                    Título de la evaluación
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="Evaluacion_Titulo"
                                                    value={evaluacionForm.Evaluacion_Titulo}
                                                    onChange={handleEvaluacionFieldChange}
                                                    disabled={savingVideo}
                                                />
                                            </div>

                                            <div className="col-md-6">
                                                <label className="form-label">
                                                    Descripción de la evaluación
                                                </label>
                                                <textarea
                                                    className="form-control"
                                                    name="Evaluacion_Descripcion"
                                                    rows="2"
                                                    value={evaluacionForm.Evaluacion_Descripcion}
                                                    onChange={handleEvaluacionFieldChange}
                                                    disabled={savingVideo}
                                                />
                                            </div>
                                        </div>

                                        {evaluacionForm.preguntas.map((pregunta, preguntaIndex) => (
                                            <div
                                                key={`pregunta-${preguntaIndex}`}
                                                className="card mb-3 shadow-sm"
                                            >
                                                <div className="card-header d-flex justify-content-between align-items-center">
                                                    <h6 className="mb-0">
                                                        Pregunta {preguntaIndex + 1}
                                                    </h6>

                                                    <button
                                                        type="button"
                                                        className="btn btn-danger btn-sm"
                                                        onClick={() => removePregunta(preguntaIndex)}
                                                        disabled={savingVideo}
                                                    >
                                                        Eliminar
                                                    </button>
                                                </div>

                                                <div className="card-body">
                                                    <div className="row mb-3">
                                                        <div className="col-md-6">
                                                            <label className="form-label">
                                                                Texto de la pregunta
                                                            </label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                value={pregunta.Pregunta_Texto}
                                                                onChange={(e) =>
                                                                    handlePreguntaChange(
                                                                        preguntaIndex,
                                                                        "Pregunta_Texto",
                                                                        e.target.value
                                                                    )
                                                                }
                                                                disabled={savingVideo}
                                                            />
                                                        </div>

                                                        <div className="col-md-2">
                                                            <label className="form-label">Orden</label>
                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                min="1"
                                                                value={pregunta.Pregunta_Orden}
                                                                onChange={(e) =>
                                                                    handlePreguntaChange(
                                                                        preguntaIndex,
                                                                        "Pregunta_Orden",
                                                                        e.target.value
                                                                    )
                                                                }
                                                                disabled={savingVideo}
                                                            />
                                                        </div>

                                                        <div className="col-md-2">
                                                            <label className="form-label">
                                                                Puntos estreno
                                                            </label>
                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                min="0"
                                                                value={pregunta.Pregunta_Puntos_Estreno}
                                                                onChange={(e) =>
                                                                    handlePreguntaChange(
                                                                        preguntaIndex,
                                                                        "Pregunta_Puntos_Estreno",
                                                                        e.target.value
                                                                    )
                                                                }
                                                                disabled={savingVideo}
                                                            />
                                                        </div>

                                                        <div className="col-md-2">
                                                            <label className="form-label">
                                                                Puntos normal
                                                            </label>
                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                min="0"
                                                                value={pregunta.Pregunta_Puntos_Normal}
                                                                onChange={(e) =>
                                                                    handlePreguntaChange(
                                                                        preguntaIndex,
                                                                        "Pregunta_Puntos_Normal",
                                                                        e.target.value
                                                                    )
                                                                }
                                                                disabled={savingVideo}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="row">
                                                        {pregunta.respuestas.map((respuesta, respuestaIndex) => (
                                                            <div
                                                                key={`pregunta-${preguntaIndex}-respuesta-${respuestaIndex}`}
                                                                className="col-md-6 mb-3"
                                                            >
                                                                <label className="form-label">
                                                                    Respuesta {respuestaIndex + 1}
                                                                </label>

                                                                <div className="input-group">
                                                                    <span className="input-group-text">
                                                                        <input
                                                                            type="radio"
                                                                            name={`correcta-${preguntaIndex}`}
                                                                            checked={
                                                                                Number(respuesta.Respuesta_Correcta) === 1
                                                                            }
                                                                            onChange={() =>
                                                                                handleCorrectaChange(
                                                                                    preguntaIndex,
                                                                                    respuestaIndex
                                                                                )
                                                                            }
                                                                            disabled={savingVideo}
                                                                        />
                                                                    </span>

                                                                    <input
                                                                        type="text"
                                                                        className="form-control"
                                                                        value={respuesta.Respuesta_Texto}
                                                                        onChange={(e) =>
                                                                            handleRespuestaChange(
                                                                                preguntaIndex,
                                                                                respuestaIndex,
                                                                                e.target.value
                                                                            )
                                                                        }
                                                                        placeholder={`Escribe la respuesta ${respuestaIndex + 1}`}
                                                                        disabled={savingVideo}
                                                                    />
                                                                </div>

                                                                <small className="text-muted">
                                                                    Marca el radio para indicar cuál es la correcta.
                                                                </small>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                    )}
                                </div>
                            )}

                            {/* ¿Este video tiene recursos? */}
                            <div className="row mb-4">
                                <div className="col-md-12">
                                    <div className="mb-3 form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="videoRecursos"
                                            name="Video_Tiene_Recursos"
                                            checked={videoForm.Video_Tiene_Recursos === 1}
                                            onChange={handleVideoInputChange}
                                            disabled={savingVideo}
                                        />
                                        <label className="form-check-label" htmlFor="videoRecursos">
                                            ¿Este video tendrá recursos?
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {Number(videoForm.Video_Tiene_Recursos) === 1 && (
                                <div className="border rounded p-3 mb-4 bg-light-subtle">
                                    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                                        <div>
                                            <h5 className="mb-0">Configuración de recursos</h5>
                                            {!showResourcesConfig && (
                                                <small className="text-muted">
                                                    {videoResources.length} recurso(s) configurado(s)
                                                </small>
                                            )}
                                        </div>

                                        <div className="d-flex gap-2">
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary btn-sm"
                                                onClick={() => setShowResourcesConfig((prev) => !prev)}
                                                disabled={savingVideo}
                                            >
                                                {showResourcesConfig ? "Ocultar" : "Mostrar"}
                                            </button>

                                            <button
                                                type="button"
                                                className="btn btn-primary btn-sm"
                                                onClick={addResource}
                                                disabled={savingVideo || !showResourcesConfig}
                                            >
                                                Agregar recurso
                                            </button>
                                        </div>
                                    </div>

                                    {showResourcesConfig && (
                                        <>
                                            {videoResources.length === 0 ? (
                                                <div className="alert alert-light border mb-0">
                                                    No hay recursos agregados todavía.
                                                </div>
                                            ) : (
                                                <div className="d-flex flex-column gap-3">
                                                    {videoResources.map((resource, index) => (
                                                        <div key={resource.tempId} className="border rounded p-3 bg-white">
                                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                                <h6 className="mb-0">Recurso #{index + 1}</h6>

                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-danger btn-sm"
                                                                    onClick={() => removeResource(resource.tempId)}
                                                                    disabled={savingVideo}
                                                                >
                                                                    Eliminar
                                                                </button>
                                                            </div>

                                                            <div className="row g-3">
                                                                <div className="col-md-6">
                                                                    <label className="form-label">Título del recurso</label>
                                                                    <input
                                                                        type="text"
                                                                        className="form-control"
                                                                        value={resource.Recurso_Titulo}
                                                                        onChange={(e) =>
                                                                            handleResourceChange(
                                                                                resource.tempId,
                                                                                "Recurso_Titulo",
                                                                                e.target.value
                                                                            )
                                                                        }
                                                                        disabled={savingVideo}
                                                                    />
                                                                </div>

                                                                <div className="col-md-3">
                                                                    <label className="form-label">Tipo</label>
                                                                    <select
                                                                        className="form-select"
                                                                        value={resource.Recurso_Tipo}
                                                                        onChange={(e) =>
                                                                            handleResourceTypeChange(resource.tempId, e.target.value)
                                                                        }
                                                                        disabled={savingVideo}
                                                                    >
                                                                        <option value="link">Link</option>
                                                                        <option value="archivo">Archivo</option>
                                                                    </select>
                                                                </div>

                                                                <div className="col-md-3">
                                                                    <label className="form-label">Orden</label>
                                                                    <input
                                                                        type="number"
                                                                        min="1"
                                                                        className="form-control"
                                                                        value={resource.Recurso_Orden}
                                                                        onChange={(e) =>
                                                                            handleResourceChange(
                                                                                resource.tempId,
                                                                                "Recurso_Orden",
                                                                                e.target.value
                                                                            )
                                                                        }
                                                                        disabled={savingVideo}
                                                                    />
                                                                </div>

                                                                <div className="col-md-6">
                                                                    <label className="form-label">Imagen del recurso</label>
                                                                    <input
                                                                        type="file"
                                                                        className="form-control"
                                                                        accept="image/*"
                                                                        onChange={(e) =>
                                                                            handleResourceImageFileChange(
                                                                                resource.tempId,
                                                                                e.target.files?.[0] || null
                                                                            )
                                                                        }
                                                                        disabled={savingVideo}
                                                                    />
                                                                </div>

                                                                {resource.Recurso_Tipo === "link" ? (
                                                                    <div className="col-md-6">
                                                                        <label className="form-label">URL del recurso</label>
                                                                        <input
                                                                            type="text"
                                                                            className="form-control"
                                                                            value={resource.Recurso_URL}
                                                                            onChange={(e) =>
                                                                                handleResourceChange(
                                                                                    resource.tempId,
                                                                                    "Recurso_URL",
                                                                                    e.target.value
                                                                                )
                                                                            }
                                                                            disabled={savingVideo}
                                                                            placeholder="https://..."
                                                                        />
                                                                    </div>
                                                                ) : (
                                                                    <div className="col-md-6">
                                                                        <label className="form-label">Archivo del recurso</label>
                                                                        <input
                                                                            type="file"
                                                                            className="form-control"
                                                                            onChange={(e) =>
                                                                                handleResourceFileChange(
                                                                                    resource.tempId,
                                                                                    e.target.files?.[0] || null
                                                                                )
                                                                            }
                                                                            disabled={savingVideo}
                                                                        />
                                                                        {resource.Recurso_Nombre_Archivo && (
                                                                            <small className="text-muted d-block mt-1">
                                                                                Archivo seleccionado: {resource.Recurso_Nombre_Archivo}
                                                                            </small>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}

                            

                            {successMessage && (
                                <div
                                    className="mb-3 alert alert-left alert-success alert-dismissible fade show"
                                    role="alert"
                                >
                                    <span>{successMessage}</span>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        aria-label="Close"
                                        onClick={() => setSuccessMessage("")}
                                    ></button>
                                </div>
                            )}

                            {infoMessage && (
                                <div
                                    className="mb-3 alert alert-top alert-info alert-dismissible fade show"
                                    role="alert"
                                >
                                    <span>{infoMessage}</span>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        aria-label="Close"
                                        onClick={() => setInfoMessage("")}
                                    ></button>
                                </div>
                            )}

                            {errorMessage && (
                                <div
                                    className="alert alert-bottom alert-danger alert-dismissible fade show"
                                    role="alert"
                                >
                                    <span>{errorMessage}</span>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        aria-label="Close"
                                        onClick={() => setErrorMessage("")}
                                    ></button>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-danger"
                                data-bs-dismiss="modal"
                                disabled={savingVideo}
                                onClick={() => {
                                    resetMessages();
                                    resetVideoForm();
                                }}
                            >
                                Cerrar
                            </button>

                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={savingVideo}
                            >
                                {savingVideo ? "Guardando..." : "Agregar"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        {/* Modal para Ver Video */}
        <div
            className="modal fade"
            id="viewVideoModal"
            tabIndex="-1"
            aria-labelledby="viewVideoModalLabel"
            aria-hidden="true"
        >
            <div className="modal-dialog modal-dialog-centered modal-xl">
                <div className="modal-content">
                    <div>
                        <div className="modal-header">
                            <h5 className="modal-title" id="viewVideoModalLabel">
                                Video
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            ></button>
                        </div>

                        <div className="modal-body">
                            {loadingViewVideo ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Cargando...</span>
                                    </div>
                                    <p className="mt-3 mb-0">Cargando detalle del video...</p>
                                </div>
                            ) : (
                                <>
                                    <div className="row mb-3">
                                        <div className="col-md-12">
                                            <div className="bd-example text-center">
                                                <img
                                                    src={
                                                        selectedVideo?.Video_Miniatura ||
                                                        "../../assets/images/pro/plugins/img7.jpg"
                                                    }
                                                    className="img-fluid rounded effect-1"
                                                    alt={selectedVideo?.Video_Titulo || "Video"}
                                                    loading="lazy"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row mb-3">
                                        <div className="col-md-12">
                                            <h4>{selectedVideo?.Video_Titulo || "Título del Video"}</h4>

                                            <label className="form-label me-4">
                                                Experto:{" "}
                                                <span className="text-primary">
                                                    {selectedVideo?.Instructor_Nombre || "Sin experto"}
                                                </span>
                                            </label>

                                            <label className="form-label me-4">
                                                <svg
                                                    className="me-2"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="18"
                                                    height="18"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        fillRule="evenodd"
                                                        clipRule="evenodd"
                                                        d="M21.25 12.0005C21.25 17.1095 17.109 21.2505 12 21.2505C6.891 21.2505 2.75 17.1095 2.75 12.0005C2.75 6.89149 6.891 2.75049 12 2.75049C17.109 2.75049 21.25 6.89149 21.25 12.0005Z"
                                                        stroke="currentColor"
                                                        strokeWidth="1.5"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                    <path
                                                        d="M15.4316 14.9429L11.6616 12.6939V7.84692"
                                                        stroke="currentColor"
                                                        strokeWidth="1.5"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                                {selectedVideo?.Video_Duracion_Seg
                                                    ? `${selectedVideo.Video_Duracion_Seg} min`
                                                    : "Sin duración"}
                                            </label>

                                            <div className="mt-2">
                                                {Number(selectedVideo?.Video_Tiene_Evaluacion) === 1 ? (
                                                    <span className="badge bg-success-subtle text-success me-2 p-2">
                                                        Con evaluación
                                                    </span>
                                                ) : (
                                                    <span className="badge bg-light text-dark me-2 p-2">
                                                        Sin evaluación
                                                    </span>
                                                )}

                                                {Number(selectedVideo?.Video_Tiene_Recursos) === 1 ? (
                                                    <span className="badge bg-primary-subtle text-primary p-2">
                                                        Con recursos
                                                    </span>
                                                ) : (
                                                    <span className="badge bg-light text-dark p-2">
                                                        Sin recursos
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row mb-4">
                                        <div className="col-md-12">
                                            <div
                                                className="ql-editor bg-light rounded border"
                                                dangerouslySetInnerHTML={{
                                                    __html:
                                                        selectedVideo?.Video_Descripcion ||
                                                        "<p>Sin descripción disponible.</p>",
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {Array.isArray(selectedVideo?.tags) && selectedVideo.tags.length > 0 && (
                                        <div className="row mb-4">
                                            <div className="col-12">
                                                <h5 className="mb-3">Tags</h5>

                                                <div className="d-flex flex-wrap gap-2">
                                                    {selectedVideo.tags.map((tag) => (
                                                        <span
                                                            key={tag.Tag_ID}
                                                            className="badge bg-secondary"
                                                        >
                                                            {tag.Tag_Nombre}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {Number(selectedVideo?.Video_Tiene_Evaluacion) === 1 && (
                                        <div className="row mb-4">
                                            <div className="col-12">
                                                <div className="mb-3">
                                                    <h5 className="mb-1">Evaluación del video</h5>
                                                </div>

                                                {Array.isArray(selectedVideo?.evaluacion?.preguntas) &&
                                                selectedVideo.evaluacion.preguntas.length > 0 ? (
                                                    selectedVideo.evaluacion.preguntas.map(
                                                        (pregunta, preguntaIndex) => (
                                                            <div
                                                                key={pregunta.Pregunta_ID || preguntaIndex}
                                                                className="card mb-3 shadow-sm"
                                                            >
                                                                <div className="card-header">
                                                                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                                                                        <h6 className="mb-0">
                                                                            Pregunta {pregunta.Pregunta_Orden}
                                                                        </h6>

                                                                        <div className="d-flex flex-wrap gap-2">
                                                                            <span className="badge bg-warning-subtle text-warning p-2">
                                                                                Puntos estreno:{" "}
                                                                                {pregunta.Pregunta_Puntos_Estreno}
                                                                            </span>
                                                                            <span className="badge bg-primary-subtle text-primary p-2">
                                                                                Puntos normal:{" "}
                                                                                {pregunta.Pregunta_Puntos_Normal}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div className="card-body">
                                                                    <p className="fw-semibold mb-3">
                                                                        {pregunta.Pregunta_Texto}
                                                                    </p>

                                                                    <div className="row">
                                                                        {Array.isArray(pregunta.respuestas) &&
                                                                            pregunta.respuestas.map(
                                                                                (respuesta, respuestaIndex) => (
                                                                                    <div
                                                                                        className="col-md-6 mb-3"
                                                                                        key={
                                                                                            respuesta.Respuesta_ID ||
                                                                                            `${preguntaIndex}-${respuestaIndex}`
                                                                                        }
                                                                                    >
                                                                                        <div
                                                                                            className={`border rounded p-3 h-100 ${
                                                                                                Number(
                                                                                                    respuesta.Respuesta_Correcta
                                                                                                ) === 1
                                                                                                    ? "border-success bg-success-subtle"
                                                                                                    : "border-light bg-white"
                                                                                            }`}
                                                                                        >
                                                                                            <div className="d-flex justify-content-between align-items-start gap-2">
                                                                                                <span>
                                                                                                    {respuesta.Respuesta_Texto}
                                                                                                </span>

                                                                                                {Number(
                                                                                                    respuesta.Respuesta_Correcta
                                                                                                ) === 1 && (
                                                                                                    <span className="badge bg-success">
                                                                                                        Correcta
                                                                                                    </span>
                                                                                                )}
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                )
                                                                            )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )
                                                    )
                                                ) : (
                                                    <div className="alert alert-light border">
                                                        Este video tiene evaluación activa, pero no se encontraron preguntas.
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {Number(selectedVideo?.Video_Tiene_Recursos) === 1 && (
                                        <div className="row mb-3">
                                            <div className="col-12">
                                                <div className="mb-3">
                                                    <h5 className="mb-1">Recursos del video</h5>
                                                </div>

                                                {Array.isArray(selectedVideo?.resources) &&
                                                selectedVideo.resources.length > 0 ? (
                                                    <div className="row">
                                                        {selectedVideo.resources.map((resource, resourceIndex) => (
                                                            <div
                                                                className="col-md-6 col-lg-4 mb-3"
                                                                key={resource.Recurso_ID || resourceIndex}
                                                            >
                                                                <div className="card h-100 shadow-sm">
                                                                    <div className="card-body">
                                                                        <div className="mb-3">
                                                                            {resource.Recurso_Imagen ? (
                                                                                <img
                                                                                    src={resource.Recurso_Imagen}
                                                                                    alt={resource.Recurso_Titulo || "Recurso"}
                                                                                    className="img-fluid rounded border"
                                                                                    style={{
                                                                                        width: "100%",
                                                                                        height: "180px",
                                                                                        objectFit: "cover",
                                                                                    }}
                                                                                />
                                                                            ) : (
                                                                                <div
                                                                                    className="border rounded d-flex align-items-center justify-content-center text-muted bg-light"
                                                                                    style={{
                                                                                        width: "100%",
                                                                                        height: "180px",
                                                                                    }}
                                                                                >
                                                                                    Sin imagen
                                                                                </div>
                                                                            )}
                                                                        </div>

                                                                        <h6 className="mb-2">
                                                                            {resource.Recurso_Titulo || "Recurso sin título"}
                                                                        </h6>

                                                                        {resource.Recurso_Nombre_Archivo && (
                                                                            <p className="mb-2 text-muted small">
                                                                                Archivo: {resource.Recurso_Nombre_Archivo}
                                                                            </p>
                                                                        )}

                                                                        <a
                                                                            href={resource.Recurso_URL}
                                                                            target="_blank"
                                                                            rel="noreferrer"
                                                                            className="btn btn-primary btn-sm"
                                                                        >
                                                                            Abrir recurso
                                                                        </a>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="alert alert-light border">
                                                        Este video tiene recursos activos, pero no se encontraron registros.
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-warning"
                                id="editVideoModalLabel"
                                data-bs-toggle="modal"
                                data-bs-target="#editVideoModal"
                                onClick={() => selectedVideo && handleOpenEditVideo(selectedVideo)}
                            >
                                Editar
                            </button>

                            <button
                                type="button"
                                className="btn btn-primary"
                                data-bs-dismiss="modal"
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {/* Modal para editar Video */}
        <div
            className="modal fade"
            id="editVideoModal"
            tabIndex="-1"
            aria-labelledby="editVideoModalLabel"
            aria-hidden="true"
        >
            <div className="modal-dialog modal-dialog-centered modal-xl">
                <div className="modal-content">
                    <form onSubmit={handleSubmitEditVideo}>
                        <div className="modal-header">
                            <h5 className="modal-title" id="editVideoModalLabel">
                                Editar Video
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                                onClick={() => {
                                    resetEditMessages();
                                }}
                            ></button>
                        </div>

                        <div className="modal-body">
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <label className="form-label" htmlFor="editTitulo">
                                        Título: <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        id="editTitulo"
                                        name="Video_Titulo"
                                        type="text"
                                        className="form-control"
                                        value={editVideoForm.Video_Titulo}
                                        onChange={handleEditVideoInputChange}
                                        disabled={savingEditVideo}
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label" htmlFor="editExperto">
                                        Experto: <span className="text-danger">*</span>
                                    </label>
                                    <select
                                        id="editExperto"
                                        name="Instructor_ID"
                                        className="form-select"
                                        value={editVideoForm.Instructor_ID}
                                        onChange={handleEditVideoInputChange}
                                        disabled={savingEditVideo || loadingInstructors}
                                    >
                                        <option value="">
                                            {loadingInstructors
                                                ? "Cargando expertos..."
                                                : "Selecciona un experto"}
                                        </option>

                                        {instructors.map((instructor) => (
                                            <option
                                                key={instructor.Instructor_ID}
                                                value={instructor.Instructor_ID}
                                            >
                                                {instructor.Instructor_Nombre}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="row mb-3">
                                <div className="col">
                                    <label className="form-label">
                                        Descripción: <span className="text-danger">*</span>
                                    </label>

                                    <div className="border rounded video-quill-wrapper">
                                        <ReactQuill
                                            theme="snow"
                                            value={editVideoForm.Video_Descripcion}
                                            onChange={handleEditDescriptionChange}
                                            placeholder="Escribe la descripción del video..."
                                            readOnly={savingEditVideo}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="row mb-2">
                                <div className="col-md-6">
                                    {editVideoForm.Current_Video_Miniatura && (
                                        <div className="mb-2">
                                            <small className="text-muted d-block mb-1">
                                                Miniatura actual
                                            </small>
                                            <img
                                                src={editVideoForm.Current_Video_Miniatura}
                                                alt="Miniatura actual"
                                                className="img-fluid rounded border"
                                                style={{ maxHeight: "120px", objectFit: "cover" }}
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="col-md-6">
                                    {editVideoForm.Current_Video_Miniatura_Sitio && (
                                        <div className="mb-2">
                                            <small className="text-muted d-block mb-1">
                                                Miniatura actual del sitio
                                            </small>
                                            <img
                                                src={editVideoForm.Current_Video_Miniatura_Sitio}
                                                alt="Miniatura actual del sitio"
                                                className="img-fluid rounded border"
                                                style={{ maxHeight: "120px", objectFit: "cover" }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <label className="form-label" htmlFor="editCustomFileVideo">
                                        Selecciona una miniatura nueva
                                    </label>
                                    <input
                                        className="form-control"
                                        type="file"
                                        id="editCustomFileVideo"
                                        name="Video_Miniatura"
                                        accept=".jpg,.jpeg,.png,.webp"
                                        onChange={handleEditVideoInputChange}
                                        disabled={savingEditVideo}
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label" htmlFor="editCustomFileVideoSite">
                                        Selecciona una miniatura nueva para el sitio
                                    </label>
                                    <input
                                        className="form-control"
                                        type="file"
                                        id="editCustomFileVideoSite"
                                        name="Video_Miniatura_Sitio"
                                        accept=".jpg,.jpeg,.png,.webp"
                                        onChange={handleEditVideoInputChange}
                                        disabled={savingEditVideo}
                                    />
                                </div>
                            </div>

                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <label className="form-label" htmlFor="editVimeo">
                                        ID Vimeo: <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        id="editVimeo"
                                        name="Video_URL_Vimeo"
                                        type="text"
                                        className="form-control"
                                        value={editVideoForm.Video_URL_Vimeo}
                                        onChange={handleEditVideoInputChange}
                                        disabled={savingEditVideo}
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label" htmlFor="editDuracionAprox">
                                        Duración Aprox: <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        id="editDuracionAprox"
                                        name="Video_Duracion_Seg"
                                        type="text"
                                        className="form-control"
                                        value={editVideoForm.Video_Duracion_Seg}
                                        onChange={handleEditVideoInputChange}
                                        disabled={savingEditVideo}
                                    />
                                </div>
                            </div>

                            <div className="row mb-4">
                                <div className="col-md-12">
                                    <label className="form-label">
                                        Tags del video
                                    </label>

                                    <div className="border rounded p-3 bg-light-subtle">
                                        {loadingTags ? (
                                            <p className="mb-0 text-muted">Cargando tags...</p>
                                        ) : tagsCatalog.length === 0 ? (
                                            <p className="mb-0 text-muted">No hay tags disponibles.</p>
                                        ) : (
                                            <div className="row">
                                                {tagsCatalog.map((tag) => (
                                                    <div className="col-md-4 mb-2" key={tag.Tag_ID}>
                                                        <div className="form-check">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                id={`tag-edit-${tag.Tag_ID}`}
                                                                checked={editSelectedTags.includes(Number(tag.Tag_ID))}
                                                                onChange={() => handleEditTagToggle(Number(tag.Tag_ID))}
                                                                disabled={savingEditVideo}
                                                            />
                                                            <label
                                                                className="form-check-label"
                                                                htmlFor={`tag-edit-${tag.Tag_ID}`}
                                                            >
                                                                <span
                                                                    className="badge me-2"
                                                                    style={{
                                                                        backgroundColor: tag.Tag_Color || "#e9ecef",
                                                                        color: "#212529",
                                                                    }}
                                                                >
                                                                    {tag.Tag_Tipo}
                                                                </span>
                                                                {tag.Tag_Nombre}
                                                            </label>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="row mb-4">
                                <div className="col-md-12">
                                    <div className="mb-3 form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="editVideoEvaluacion"
                                            name="Video_Tiene_Evaluacion"
                                            checked={editVideoForm.Video_Tiene_Evaluacion === 1}
                                            onChange={handleEditVideoInputChange}
                                            disabled={savingEditVideo}
                                        />
                                        <label className="form-check-label" htmlFor="editVideoEvaluacion">
                                            ¿Este video tendrá evaluación?
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {Number(editVideoForm.Video_Tiene_Evaluacion) === 1 && (
                                <div className="border rounded p-3 mb-4 bg-light-subtle">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5 className="mb-0">Configuración de evaluación</h5>
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-sm"
                                            onClick={addEditPregunta}
                                            disabled={savingEditVideo}
                                        >
                                            Agregar pregunta
                                        </button>
                                    </div>

                                    {/* <div className="row mb-3">
                                        <div className="col-md-6">
                                            <label className="form-label">
                                                Título de la evaluación
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="Evaluacion_Titulo"
                                                value={editEvaluacionForm.Evaluacion_Titulo}
                                                onChange={handleEditEvaluacionFieldChange}
                                                disabled={savingEditVideo}
                                            />
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label">
                                                Descripción de la evaluación
                                            </label>
                                            <textarea
                                                className="form-control"
                                                name="Evaluacion_Descripcion"
                                                rows="2"
                                                value={editEvaluacionForm.Evaluacion_Descripcion}
                                                onChange={handleEditEvaluacionFieldChange}
                                                disabled={savingEditVideo}
                                            />
                                        </div>
                                    </div> */}

                                    {editEvaluacionForm.preguntas.map((pregunta, preguntaIndex) => (
                                        <div
                                            key={`edit-pregunta-${preguntaIndex}`}
                                            className="card mb-3 shadow-sm"
                                        >
                                            <div className="card-header d-flex justify-content-between align-items-center">
                                                <h6 className="mb-0">
                                                    Pregunta {preguntaIndex + 1}
                                                </h6>

                                                <button
                                                    type="button"
                                                    className="btn btn-danger btn-sm"
                                                    onClick={() => removeEditPregunta(preguntaIndex)}
                                                    disabled={savingEditVideo}
                                                >
                                                    Eliminar
                                                </button>
                                            </div>

                                            <div className="card-body">
                                                <div className="row mb-3">
                                                    <div className="col-md-6">
                                                        <label className="form-label">
                                                            Texto de la pregunta
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={pregunta.Pregunta_Texto}
                                                            onChange={(e) =>
                                                                handleEditPreguntaChange(
                                                                    preguntaIndex,
                                                                    "Pregunta_Texto",
                                                                    e.target.value
                                                                )
                                                            }
                                                            disabled={savingEditVideo}
                                                        />
                                                    </div>

                                                    <div className="col-md-2">
                                                        <label className="form-label">Orden</label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            min="1"
                                                            value={pregunta.Pregunta_Orden}
                                                            onChange={(e) =>
                                                                handleEditPreguntaChange(
                                                                    preguntaIndex,
                                                                    "Pregunta_Orden",
                                                                    e.target.value
                                                                )
                                                            }
                                                            disabled={savingEditVideo}
                                                        />
                                                    </div>

                                                    <div className="col-md-2">
                                                        <label className="form-label">
                                                            Puntos estreno
                                                        </label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            min="0"
                                                            value={pregunta.Pregunta_Puntos_Estreno}
                                                            onChange={(e) =>
                                                                handleEditPreguntaChange(
                                                                    preguntaIndex,
                                                                    "Pregunta_Puntos_Estreno",
                                                                    e.target.value
                                                                )
                                                            }
                                                            disabled={savingEditVideo}
                                                        />
                                                    </div>

                                                    <div className="col-md-2">
                                                        <label className="form-label">
                                                            Puntos normal
                                                        </label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            min="0"
                                                            value={pregunta.Pregunta_Puntos_Normal}
                                                            onChange={(e) =>
                                                                handleEditPreguntaChange(
                                                                    preguntaIndex,
                                                                    "Pregunta_Puntos_Normal",
                                                                    e.target.value
                                                                )
                                                            }
                                                            disabled={savingEditVideo}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="row">
                                                    {pregunta.respuestas.map((respuesta, respuestaIndex) => (
                                                        <div
                                                            key={`edit-pregunta-${preguntaIndex}-respuesta-${respuestaIndex}`}
                                                            className="col-md-6 mb-3"
                                                        >
                                                            <label className="form-label">
                                                                Respuesta {respuestaIndex + 1}
                                                            </label>

                                                            <div className="input-group">
                                                                <span className="input-group-text">
                                                                    <input
                                                                        type="radio"
                                                                        name={`edit-correcta-${preguntaIndex}`}
                                                                        checked={
                                                                            Number(respuesta.Respuesta_Correcta) === 1
                                                                        }
                                                                        onChange={() =>
                                                                            handleEditCorrectaChange(
                                                                                preguntaIndex,
                                                                                respuestaIndex
                                                                            )
                                                                        }
                                                                        disabled={savingEditVideo}
                                                                    />
                                                                </span>

                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    value={respuesta.Respuesta_Texto}
                                                                    onChange={(e) =>
                                                                        handleEditRespuestaChange(
                                                                            preguntaIndex,
                                                                            respuestaIndex,
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                    placeholder={`Escribe la respuesta ${respuestaIndex + 1}`}
                                                                    disabled={savingEditVideo}
                                                                />
                                                            </div>

                                                            <small className="text-muted">
                                                                Marca el radio para indicar cuál es la correcta.
                                                            </small>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="row mb-4">
                                <div className="col-md-12">
                                    <div className="mb-3 form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="editVideoRecursos"
                                            name="Video_Tiene_Recursos"
                                            checked={editVideoForm.Video_Tiene_Recursos === 1}
                                            onChange={handleEditVideoInputChange}
                                            disabled={savingEditVideo}
                                        />
                                        <label className="form-check-label" htmlFor="editVideoRecursos">
                                            ¿Este video tendrá recursos?
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {Number(editVideoForm.Video_Tiene_Recursos) === 1 && (
                                <div className="border rounded p-3 mb-4 bg-light-subtle">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5 className="mb-0">Configuración de recursos</h5>

                                        <div className="d-flex gap-2">
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary btn-sm"
                                                onClick={() => setShowEditResourcesConfig((prev) => !prev)}
                                                disabled={savingEditVideo}
                                            >
                                                {showEditResourcesConfig ? "Ocultar" : "Mostrar"}
                                            </button>

                                            <button
                                                type="button"
                                                className="btn btn-primary btn-sm"
                                                onClick={addEditResource}
                                                disabled={savingEditVideo || !showEditResourcesConfig}
                                            >
                                                Agregar recurso
                                            </button>
                                        </div>
                                    </div>

                                    {showEditResourcesConfig && (
                                        <>
                                            {editVideoResources.length === 0 ? (
                                                <div className="alert alert-light border mb-0">
                                                    No hay recursos agregados todavía.
                                                </div>
                                            ) : (
                                                <div className="d-flex flex-column gap-3">
                                                    {editVideoResources.map((resource, index) => (
                                                        <div
                                                            key={resource.tempId}
                                                            className="card mb-3 shadow-sm"
                                                        >
                                                            <div className="card-header d-flex justify-content-between align-items-center">
                                                                <h6 className="mb-0">
                                                                    Recurso {index + 1}
                                                                </h6>

                                                                <button
                                                                    type="button"
                                                                    className="btn btn-danger btn-sm"
                                                                    onClick={() => removeEditResource(resource.tempId)}
                                                                    disabled={savingEditVideo}
                                                                >
                                                                    Eliminar
                                                                </button>
                                                            </div>

                                                            <div className="card-body">
                                                                <div className="row mb-3">
                                                                    <div className="col-md-6">
                                                                        <label className="form-label">
                                                                            Título del recurso
                                                                        </label>
                                                                        <input
                                                                            type="text"
                                                                            className="form-control"
                                                                            value={resource.Recurso_Titulo}
                                                                            onChange={(e) =>
                                                                                handleEditResourceChange(
                                                                                    resource.tempId,
                                                                                    "Recurso_Titulo",
                                                                                    e.target.value
                                                                                )
                                                                            }
                                                                            disabled={savingEditVideo}
                                                                        />
                                                                    </div>

                                                                    <div className="col-md-3">
                                                                        <label className="form-label">Tipo</label>
                                                                        <select
                                                                            className="form-select"
                                                                            value={resource.Recurso_Tipo}
                                                                            onChange={(e) =>
                                                                                handleEditResourceTypeChange(
                                                                                    resource.tempId,
                                                                                    e.target.value
                                                                                )
                                                                            }
                                                                            disabled={savingEditVideo}
                                                                        >
                                                                            <option value="link">Link</option>
                                                                            <option value="archivo">Archivo</option>
                                                                        </select>
                                                                    </div>

                                                                    <div className="col-md-3">
                                                                        <label className="form-label">Orden</label>
                                                                        <input
                                                                            type="number"
                                                                            min="1"
                                                                            className="form-control"
                                                                            value={resource.Recurso_Orden}
                                                                            onChange={(e) =>
                                                                                handleEditResourceChange(
                                                                                    resource.tempId,
                                                                                    "Recurso_Orden",
                                                                                    e.target.value
                                                                                )
                                                                            }
                                                                            disabled={savingEditVideo}
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <div className="row mb-3">
                                                                    <div className="col-md-6">
                                                                        {resource.Current_Recurso_Imagen && (
                                                                            <div className="mb-2">
                                                                                <small className="text-muted d-block mb-1">
                                                                                    Imagen actual del recurso
                                                                                </small>
                                                                                <img
                                                                                    src={resource.Current_Recurso_Imagen}
                                                                                    alt="Imagen recurso"
                                                                                    className="img-fluid rounded border"
                                                                                    style={{
                                                                                        maxHeight: "120px",
                                                                                        objectFit: "cover",
                                                                                    }}
                                                                                />
                                                                            </div>
                                                                        )}
                                                                    </div>

                                                                    <div className="col-md-6">
                                                                        {resource.Recurso_Tipo === "archivo" &&
                                                                            resource.Recurso_Nombre_Archivo && (
                                                                                <div className="mb-2">
                                                                                    <small className="text-muted d-block mb-1">
                                                                                        Archivo actual
                                                                                    </small>
                                                                                    <div className="border rounded p-2 bg-white">
                                                                                        {resource.Recurso_Nombre_Archivo}
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                    </div>
                                                                </div>

                                                                <div className="row mb-3">
                                                                    <div className="col-md-6">
                                                                        <label className="form-label">
                                                                            Nueva imagen del recurso
                                                                        </label>
                                                                        <input
                                                                            type="file"
                                                                            className="form-control"
                                                                            accept=".jpg,.jpeg,.png,.webp"
                                                                            onChange={(e) =>
                                                                                handleEditResourceImageFileChange(
                                                                                    resource.tempId,
                                                                                    e.target.files?.[0] || null
                                                                                )
                                                                            }
                                                                            disabled={savingEditVideo}
                                                                        />
                                                                    </div>

                                                                    {resource.Recurso_Tipo === "link" ? (
                                                                        <div className="col-md-6">
                                                                            <label className="form-label">
                                                                                URL del recurso
                                                                            </label>
                                                                            <input
                                                                                type="text"
                                                                                className="form-control"
                                                                                value={resource.Recurso_URL}
                                                                                onChange={(e) =>
                                                                                    handleEditResourceChange(
                                                                                        resource.tempId,
                                                                                        "Recurso_URL",
                                                                                        e.target.value
                                                                                    )
                                                                                }
                                                                                disabled={savingEditVideo}
                                                                                placeholder="https://..."
                                                                            />
                                                                        </div>
                                                                    ) : (
                                                                        <div className="col-md-6">
                                                                            <label className="form-label">
                                                                                Nuevo archivo del recurso
                                                                            </label>
                                                                            <input
                                                                                type="file"
                                                                                className="form-control"
                                                                                onChange={(e) =>
                                                                                    handleEditResourceFileChange(
                                                                                        resource.tempId,
                                                                                        e.target.files?.[0] || null
                                                                                    )
                                                                                }
                                                                                disabled={savingEditVideo}
                                                                            />

                                                                            {resource.Recurso_Nombre_Archivo && (
                                                                                <small className="text-muted d-block mt-1">
                                                                                    Archivo seleccionado: {resource.Recurso_Nombre_Archivo}
                                                                                </small>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}

                            {editSuccessMessage && (
                                <div
                                    className="mb-3 alert alert-left alert-success alert-dismissible fade show"
                                    role="alert"
                                >
                                    <span>{editSuccessMessage}</span>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        aria-label="Close"
                                        onClick={() => setEditSuccessMessage("")}
                                    ></button>
                                </div>
                            )}

                            {editInfoMessage && (
                                <div
                                    className="mb-3 alert alert-top alert-info alert-dismissible fade show"
                                    role="alert"
                                >
                                    <span>{editInfoMessage}</span>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        aria-label="Close"
                                        onClick={() => setEditInfoMessage("")}
                                    ></button>
                                </div>
                            )}

                            {editErrorMessage && (
                                <div
                                    className="alert alert-bottom alert-danger alert-dismissible fade show"
                                    role="alert"
                                >
                                    <span>{editErrorMessage}</span>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        aria-label="Close"
                                        onClick={() => setEditErrorMessage("")}
                                    ></button>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-danger"
                                data-bs-dismiss="modal"
                                disabled={savingEditVideo}
                                onClick={() => {
                                    resetEditMessages();
                                    resetEditVideoForm();
                                }}
                            >
                                Cerrar
                            </button>

                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={savingEditVideo}
                            >
                                {savingEditVideo ? "Guardando..." : "Guardar cambios"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
        
        </>
    )
}

export default VideoList